import { Component, useState, useEffect, useRef, useMemo, type CSSProperties, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  X, Package, Loader2, CheckCircle2, Upload,
  X as XIcon,
  Plus, Link2, ShoppingCart, Factory,
  LayoutList, ScrollText, Edit2, Trash2,
} from 'lucide-react';
import { Button } from '@/components/common/Button';
import { useToast } from '@/hooks/useToast';
import { usePreferencesStore } from '@/store/preferencesStore';
import client from '@/api/client';
import { uploadApi } from '@/api/uploadApi';
import { referenceDataApi } from '@/api/referenceDataApi';
import { productionMethodsApi } from '@/api/productionMethodsApi';
import { itemTypesApi } from '@/api/itemTypesApi';
import { machinesApi } from '@/api/machinesApi';
import { storageLocationsApi } from '@/api/storageLocationsApi';
import type { RefSupplier, RefColor, StorageLocation, Supply, SupplyClass, SupplyType } from '@/types/api';
import {
  APPAREL_SIZES,
  COLOR_OPTIONS,
  CRAFT_TYPES_BY_CLASS,
  FILAMENT_DIAMETERS,
  FILAMENT_MAKERS,
  FILAMENT_NET_WEIGHTS,
  FILAMENT_TYPES,
  FINISH_OPTIONS_BY_MATERIAL,
  MATERIAL_TYPES,
  SHAPE_OPTIONS,
  SUPPLY_CLASSES,
  TUMBLER_BLANK_PROFILES,
  UNITS_OF_MEASURE,
  allowedUnitsFor,
  defaultUnitForClass,
  isBlankSupplyClass,
  productionMethodMatchesSupplyClass,
  supplyClassFromType,
  visibleSteps,
  STEP_LABELS,
  type SupplyFormStep,
  type SupplyFormMode,
} from './supplyFormConstants';
import { SupplySinglePageLayout } from './SupplySinglePageLayout';

type SupplyNameTemplate = {
  name: string;
  supplyClass: SupplyClass;
  supplyType: SupplyType;
};

type ReferenceEditTarget =
  | { type: 'color'; item?: RefColor }
  | { type: 'supplier'; item?: RefSupplier }
  | { type: 'location'; item?: FlattenedStorageLocation }
  | null;

type HardwareDimensionField = 'outerDiameter' | 'innerDiameter' | 'wireThickness';

type HardwareLookupMetadata = {
  fields?: HardwareDimensionField[];
  openingStyles?: string[];
  outerLabel?: string;
  innerLabel?: string;
  wireLabel?: string;
};

type FlattenedStorageLocation = {
  id: string;
  label: string;
  name: string;
  parentId: string | null;
  locationType: StorageLocation['locationType'];
  sortOrder: number;
};

const SUPPLY_NAME_TEMPLATES: SupplyNameTemplate[] = [
  { name: 'Axolotl', supplyClass: 'CUSTOMIZABLE_BLANK', supplyType: 'OTHER_BLANK' },
  { name: 'Bear', supplyClass: 'CUSTOMIZABLE_BLANK', supplyType: 'OTHER_BLANK' },
  { name: 'Bull Dog', supplyClass: 'CUSTOMIZABLE_BLANK', supplyType: 'OTHER_BLANK' },
  { name: 'Cardinal', supplyClass: 'CUSTOMIZABLE_BLANK', supplyType: 'OTHER_BLANK' },
  { name: 'Dog Tag Blank', supplyClass: 'CUSTOMIZABLE_BLANK', supplyType: 'DOG_TAG_BLANK' },
  { name: 'Dragon', supplyClass: 'CUSTOMIZABLE_BLANK', supplyType: 'OTHER_BLANK' },
  { name: 'Ducky Unicorn', supplyClass: 'CUSTOMIZABLE_BLANK', supplyType: 'OTHER_BLANK' },
  { name: 'Frog', supplyClass: 'CUSTOMIZABLE_BLANK', supplyType: 'OTHER_BLANK' },
  { name: 'Gecko', supplyClass: 'CUSTOMIZABLE_BLANK', supplyType: 'OTHER_BLANK' },
  { name: 'Gorilla', supplyClass: 'CUSTOMIZABLE_BLANK', supplyType: 'OTHER_BLANK' },
  { name: 'Horse', supplyClass: 'CUSTOMIZABLE_BLANK', supplyType: 'OTHER_BLANK' },
  { name: 'Octopus', supplyClass: 'CUSTOMIZABLE_BLANK', supplyType: 'OTHER_BLANK' },
  { name: 'Ornament', supplyClass: 'CUSTOMIZABLE_BLANK', supplyType: 'ORNAMENT_BLANK' },
  { name: 'Paw', supplyClass: 'CUSTOMIZABLE_BLANK', supplyType: 'OTHER_BLANK' },
  { name: 'Piggy', supplyClass: 'CUSTOMIZABLE_BLANK', supplyType: 'OTHER_BLANK' },
  { name: 'Pot', supplyClass: 'CUSTOMIZABLE_BLANK', supplyType: 'OTHER_BLANK' },
  { name: 'Shirt', supplyClass: 'BLANK_APPAREL', supplyType: 'T_SHIRT' },
  { name: 'T-Shirt', supplyClass: 'BLANK_APPAREL', supplyType: 'T_SHIRT' },
  { name: 'Hoodie', supplyClass: 'BLANK_APPAREL', supplyType: 'HOODIE' },
  { name: 'Sweatshirt', supplyClass: 'BLANK_APPAREL', supplyType: 'SWEATSHIRT' },
  { name: 'Tank Top', supplyClass: 'BLANK_APPAREL', supplyType: 'TANK_TOP' },
  { name: 'Hat', supplyClass: 'BLANK_APPAREL', supplyType: 'HAT' },
  { name: 'Tote Bag', supplyClass: 'BLANK_APPAREL', supplyType: 'TOTE_BAG' },
];

const COLOR_SWATCHES: Record<string, string> = {
  Black: '#111827',
  Blue: '#2563eb',
  Brown: '#92400e',
  Clear: 'linear-gradient(135deg, #ffffff 0%, #ffffff 45%, #d1d5db 45%, #d1d5db 55%, #ffffff 55%, #ffffff 100%)',
  Gold: '#d97706',
  Gray: '#6b7280',
  Green: '#16a34a',
  Orange: '#f97316',
  Pink: '#ec4899',
  Purple: '#9333ea',
  Rainbow: 'linear-gradient(135deg, #ef4444, #f97316, #eab308, #22c55e, #3b82f6, #a855f7)',
  Red: '#dc2626',
  Silver: '#cbd5e1',
  White: '#ffffff',
  Yellow: '#facc15',
};

const METALLIC_COLORS = new Set(['Gold', 'Silver', 'Metallic', 'Brass', 'Copper', 'Steel']);
const COLOR_ORDER = [
  'Black',
  'White',
  'Gray',
  'Clear',
  'Red',
  'Orange',
  'Yellow',
  'Green',
  'Blue',
  'Purple',
  'Pink',
  'Brown',
  'Rainbow',
  'Gold',
  'Silver',
  'Copper',
  'Brass',
  'Steel',
  'Metallic',
];
const SUPPLIER_PRIORITY = ['Amazon', 'Bambu', 'Temu'];
const HIDDEN_SUPPLIER_NAMES = new Set([
  'Atomic Filament',
  'Bambu Lab',
  'Fastenal',
  'Grainger',
  'Hatch Box',
  'Hatchbox',
  'KExcelled',
  'MatterHackers',
  'McMaster-Carr',
  'Prusa Research',
].map((name) => name.toLowerCase()));

const MATERIAL_DEFAULTS: Partial<Record<string, { finish: string; treatments: string[] }>> = {
  Aluminum: { finish: 'Raw', treatments: [] },
  'Stainless Steel': { finish: 'Raw', treatments: [] },
  Steel: { finish: 'Raw', treatments: [] },
  Brass: { finish: 'Raw', treatments: [] },
  Copper: { finish: 'Raw', treatments: [] },
  Acrylic: { finish: 'Glossy', treatments: [] },
  PLA: { finish: 'Matte', treatments: [] },
  'PLA+': { finish: 'Matte', treatments: [] },
  PETG: { finish: 'Shiny', treatments: [] },
  TPU: { finish: 'Matte', treatments: [] },
  ABS: { finish: 'Matte', treatments: [] },
  ASA: { finish: 'Matte', treatments: [] },
  Resin: { finish: 'Glossy', treatments: [] },
  MDF: { finish: 'Raw', treatments: [] },
  Plywood: { finish: 'Raw', treatments: [] },
  Hardwood: { finish: 'Raw', treatments: [] },
  Bamboo: { finish: 'Raw', treatments: [] },
  Cork: { finish: 'Raw', treatments: [] },
  Cotton: { finish: '', treatments: ['Sublimation'] },
  Polyester: { finish: '', treatments: ['Sublimation'] },
  'Cotton / Polyester': { finish: '', treatments: ['DTF'] },
  'Cotton / Nylon': { finish: '', treatments: ['HTV Heat Transfer Vinyl'] },
  Canvas: { finish: '', treatments: ['Vinyl Print'] },
  Felt: { finish: '', treatments: ['Embroidery'] },
  Silk: { finish: '', treatments: ['Sublimation'] },
  Satin: { finish: '', treatments: ['Sublimation'] },
  Glitter: { finish: '', treatments: ['HTV Heat Transfer Vinyl'] },
  Metallic: { finish: '', treatments: ['HTV Heat Transfer Vinyl'] },
  Ceramic: { finish: 'Glossy', treatments: ['DTF'] },
  Glass: { finish: 'Glossy', treatments: [] },
  Paper: { finish: 'Matte', treatments: [] },
  Cardboard: { finish: 'Matte', treatments: [] },
};

const SURFACE_OPTIONS_BY_MATERIAL: Partial<Record<string, string[]>> = {
  Aluminum: ['Powder Coated', 'Anodized', 'Painted'],
  'Stainless Steel': ['Powder Coated', 'UV'],
  Steel: ['Powder Coated', 'Painted'],
  Brass: ['Painted'],
  Copper: ['Painted'],
  Acrylic: ['UV', 'Protective Film'],
  MDF: ['Painted', 'Stained', 'Sealed'],
  Plywood: ['Painted', 'Stained', 'Sealed'],
  Hardwood: ['Painted', 'Stained', 'Sealed', 'Clear Coated'],
  Bamboo: ['Stained', 'Sealed', 'Clear Coated'],
  Cork: ['Sealed'],
  Leather: ['Embroidery', 'Fabric Painting'],
  Ceramic: ['Sublimation', 'DTF', 'UV'],
  Glass: ['UV'],
  Paper: ['Printed'],
  Cardboard: ['Printed'],
};

const FILAMENT_MATERIAL_TYPES = ['ABS', 'ASA', 'Nylon', 'PC', 'PETG', 'PLA', 'PLA+', 'PVA', 'Support Material', 'TPU'];
const FILAMENT_FINISH_OPTIONS = ['Carbon Fiber Filled', 'Dual Color', 'Glitter', 'Glow', 'Gradient', 'Marble', 'Matte', 'Metallic', 'Silk', 'Standard', 'Translucent', 'Transparent', 'Tri-Color', 'Wood'];
const BLANK_MATERIAL_TYPES = [
  'Acrylic',
  'Aluminum',
  'Canvas',
  'Ceramic',
  'Cotton',
  'Cotton/Poly Blend',
  'Faux Leather',
  'Leather',
  'MDF',
  'Plastic',
  'Polyester',
  'Stainless Steel',
  'Tri-Blend',
  'Wood',
];
const APPAREL_MATERIAL_TYPES = ['Cotton', 'Polyester', 'Cotton / Polyester', 'Cotton / Nylon', 'Canvas', 'Felt', 'Silk', 'Satin', 'Glitter', 'Metallic'];
const APPAREL_SURFACE_OPTIONS = [
  'Sublimation',
  'DTF',
  'UV',
  'Vinyl Print',
  'HTV Heat Transfer Vinyl',
  'Embroidery',
  'Fabric Painting',
  'Stencilling',
  'Tie-Dye',
];

const HARDWARE_TYPES = [
  'Bail',
  'Ball Chain',
  'Brooch Pin',
  'Buckle',
  'Chain',
  'Clasp',
  'Clip',
  'Eyelet',
  'Grommet',
  'Hook',
  'Jump Ring',
  'Key Ring',
  'Magnet',
  'Nut',
  'Pin Back',
  'Rivet',
  'Screw',
  'Snap',
  'Split Ring',
  'Washer',
];

const HARDWARE_MATERIAL_TYPES = [
  'Aluminum',
  'Brass',
  'Copper',
  'Nickel',
  'Plastic',
  'Stainless Steel',
  'Steel',
  'Zinc Alloy',
];

const HARDWARE_FINISH_OPTIONS = [
  'Brushed',
  'Matte',
  'Painted',
  'Plated',
  'Polished',
  'Powder Coated',
  'Raw',
];

const HARDWARE_FINISH_COLOR_OPTIONS = [
  'Black',
  'Bronze',
  'Copper',
  'Gold',
  'Gunmetal',
  'Rainbow',
  'Rose Gold',
  'Silver',
];

const HARDWARE_FIELDS_BY_TYPE: Partial<Record<string, HardwareDimensionField[]>> = {
  Bail: ['outerDiameter', 'innerDiameter'],
  'Ball Chain': ['outerDiameter'],
  Grommet: ['innerDiameter', 'outerDiameter'],
  'Jump Ring': ['outerDiameter', 'innerDiameter', 'wireThickness'],
  'Key Ring': ['outerDiameter', 'wireThickness'],
  Magnet: ['outerDiameter', 'wireThickness'],
  'Split Ring': ['outerDiameter', 'wireThickness'],
};

const SHAPE_BY_SUPPLY_TYPE: Partial<Record<SupplyType, string>> = {
  DOG_TAG_BLANK: 'Bone',
  ORNAMENT_BLANK: 'Circle',
  KEYCHAIN_BLANK: 'Custom',
  TUMBLER_BLANK: 'Custom',
  MAGNET_BLANK: 'Custom',
  SIGN_BLANK: 'Rectangle',
  BUTTON_BLANK: 'Circle',
  COASTER_BLANK: 'Square',
  STICKER_BLANK: 'Custom',
  DECAL_BLANK: 'Custom',
  ACRYLIC_SHEET: 'Rectangle',
  MDF_SHEET: 'Rectangle',
  PLYWOOD_SHEET: 'Rectangle',
  STAINLESS_STEEL_SHEET: 'Rectangle',
  ALUMINUM_SHEET: 'Rectangle',
};

const STORAGE_LOCATION_PREFIXES: Record<string, string> = {
  'Studio A': 'SA',
  'Studio B': 'SB',
  'Home Office': 'HO',
  Patio: 'P',
  Garage: 'GA',
  '7 Avenue Store': '7A',
  '7 Ave Store': '7A',
  'Glendale Store': 'GS',
};

function swatchStyle(color: string): CSSProperties {
  const swatch = COLOR_SWATCHES[color] ?? color;
  return swatch.includes('gradient')
    ? { background: swatch }
    : { backgroundColor: swatch };
}

function colorRank(color: string): number {
  const index = COLOR_ORDER.findIndex((value) => value.toLowerCase() === color.toLowerCase());
  return index === -1 ? COLOR_ORDER.length : index;
}

function isMetallicColor(color: string): boolean {
  const normalized = color.toLowerCase();
  return METALLIC_COLORS.has(color) || ['gold', 'silver', 'metallic', 'brass', 'copper', 'steel'].some((value) => normalized.includes(value));
}

function inferTemplateFromName(rawName: string): SupplyNameTemplate | undefined {
  const query = rawName.trim().toLowerCase();
  if (!query) return undefined;

  return SUPPLY_NAME_TEMPLATES.find((template) => {
    const templateName = template.name.toLowerCase();
    return query === templateName || query.startsWith(`${templateName} `);
  });
}

function normalizeImageUrl(url: string | null): string | null {
  if (!url) return null;
  try {
    const parsed = new URL(url, window.location.origin);
    if (parsed.pathname.startsWith('/api/uploads/')) return `${parsed.pathname}${parsed.search}`;
    if (parsed.pathname.startsWith('/uploads/')) return `/api${parsed.pathname}${parsed.search}`;
    return url;
  } catch {
    return url.startsWith('/uploads/') ? `/api${url}` : url;
  }
}

function supplierRank(name: string): number {
  const normalized = name.toLowerCase();
  const priorityIndex = SUPPLIER_PRIORITY.findIndex((item) => normalized.startsWith(item.toLowerCase()));
  return priorityIndex === -1 ? SUPPLIER_PRIORITY.length : priorityIndex;
}

function sortSuppliersForEntry(list: RefSupplier[]): RefSupplier[] {
  return [...list]
    .filter((supplier) => !HIDDEN_SUPPLIER_NAMES.has(supplier.name.toLowerCase()))
    .sort((a, b) =>
      supplierRank(a.name) - supplierRank(b.name)
      || (a.sortOrder ?? 100) - (b.sortOrder ?? 100)
      || a.name.localeCompare(b.name)
    );
}

function displayStorageLocationLabel(label: string): string {
  const parts = label.split(' > ').map((part) => part.trim()).filter(Boolean);
  const root = parts[0] ?? label;
  const child = parts[parts.length - 1];
  const prefix = STORAGE_LOCATION_PREFIXES[root];
  if (!prefix || !child || child === root) return root;
  const match = child.match(/(\d+)/);
  return match ? `${root} = ${prefix}${prefix === 'P' ? '' : ' '}${match[1]}` : `${root} = ${child}`;
}

function inferredProductionTerms(materialType: string, surfaceTreatment: string): string[] {
  const surface = surfaceTreatment.toLowerCase();
  const material = materialType.toLowerCase();
  if (surface.includes('dtf')) return ['dtf'];
  if (surface.includes('uv')) return ['uv'];
  if (surface.includes('sublimation')) return ['sublimation'];
  if (surface.includes('embroidery')) return ['embroider'];
  if (surface.includes('htv') || surface.includes('vinyl')) return ['vinyl', 'heat transfer'];
  if (surface.includes('paint') || surface.includes('stencil') || surface.includes('tie-dye')) return ['paint', 'apparel'];
  if (material.includes('pla') || material.includes('petg') || material.includes('tpu') || material.includes('abs') || material.includes('asa')) return ['3d'];
  if (['mdf', 'plywood', 'hardwood', 'bamboo', 'cork', 'acrylic'].some((value) => material.includes(value))) return ['laser'];
  if (['steel', 'aluminum', 'brass', 'copper'].some((value) => material.includes(value))) return ['laser', 'engrave'];
  return [];
}

function parseHardwareLookupMetadata(metadataJson?: string | null): HardwareLookupMetadata {
  if (!metadataJson) return {};
  try {
    const parsed = JSON.parse(metadataJson) as HardwareLookupMetadata;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function lookupLabels(lookups: { category: string; label: string }[], category: string): string[] {
  return lookups.filter((lookup) => lookup.category === category).map((lookup) => lookup.label);
}

function withLookupFallback(lookupValues: string[], fallbackValues: readonly string[]): string[] {
  return Array.from(new Set([...(lookupValues.length ? lookupValues : fallbackValues)]));
}

interface EditSupplyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  supply?: unknown;
}

interface QuickAddModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (name: string, extra?: string, secondExtra?: string) => Promise<void>;
  title: string;
  placeholder: string;
  initialName?: string;
  initialExtra?: string;
  initialSecondExtra?: string;
  submitLabel?: string;
  extraLabel?: string;
  extraPlaceholder?: string;
  secondExtraLabel?: string;
  secondExtraPlaceholder?: string;
}

function QuickAddModal({
  isOpen,
  onClose,
  onSave,
  title,
  placeholder,
  initialName = '',
  initialExtra = '',
  initialSecondExtra = '',
  submitLabel = 'Add',
  extraLabel,
  extraPlaceholder,
  secondExtraLabel,
  secondExtraPlaceholder,
}: QuickAddModalProps) {
  const [name, setName] = useState(initialName);
  const [extra, setExtra] = useState(initialExtra);
  const [secondExtra, setSecondExtra] = useState(initialSecondExtra);
  const [loading, setLoading] = useState(false);
  const { success: showSuccess, error: showError } = useToast();
  useEffect(() => {
    if (!isOpen) return;
    setName(initialName);
    setExtra(initialExtra);
    setSecondExtra(initialSecondExtra);
  }, [initialExtra, initialName, initialSecondExtra, isOpen]);
  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setLoading(true);
    try {
      await onSave(name.trim(), extra.trim() || undefined, secondExtra.trim() || undefined);
      showSuccess(`${title} saved successfully`);
      setName('');
      setExtra('');
      setSecondExtra('');
      onClose();
    } catch (err: unknown) {
      showError(err instanceof Error ? err.message : `Failed to add ${title.toLowerCase()}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-sm bg-white dark:bg-charcoal-900 border border-gray-200 dark:border-charcoal-700 rounded-xl shadow-2xl p-6">
        <h3 className="font-display text-lg font-semibold text-gray-900 dark:text-warm-cream mb-4">{title}</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={placeholder}
            required
            autoFocus
            className="w-full px-3 py-2 bg-white dark:bg-charcoal-800 border border-gray-300 dark:border-charcoal-600 rounded-lg"
          />
          {extraLabel && (
            <input
              type="text"
              value={extra}
              onChange={(e) => setExtra(e.target.value)}
              placeholder={extraPlaceholder}
              className="w-full px-3 py-2 bg-white dark:bg-charcoal-800 border border-gray-300 dark:border-charcoal-600 rounded-lg"
            />
          )}
          {secondExtraLabel && (
            <input
              type="number"
              value={secondExtra}
              onChange={(e) => setSecondExtra(e.target.value)}
              placeholder={secondExtraPlaceholder}
              className="w-full px-3 py-2 bg-white dark:bg-charcoal-800 border border-gray-300 dark:border-charcoal-600 rounded-lg"
            />
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={onClose} disabled={loading}>Cancel</Button>
            <Button type="submit" variant="primary" disabled={loading || !name.trim()}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : submitLabel}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function flattenStorageLocations(
  locations: StorageLocation[],
  parentPath = ''
): FlattenedStorageLocation[] {
  return locations.flatMap((location) => {
    if (location.name === 'Supply Closet') return [];
    const path = parentPath ? `${parentPath} > ${location.name}` : location.name;
    return [
      {
        id: location.id,
        label: path,
        name: location.name,
        parentId: location.parentId,
        locationType: location.locationType,
        sortOrder: location.sortOrder,
      },
      ...flattenStorageLocations(location.children ?? [], path),
    ];
  });
}

class PhotoStepBoundary extends Component<{ children: ReactNode; resetKey: string }, { hasError: boolean }> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidUpdate(prevProps: { resetKey: string }) {
    if (prevProps.resetKey !== this.props.resetKey && this.state.hasError) {
      this.setState({ hasError: false });
    }
  }

  componentDidCatch(error: unknown) {
    console.error('Supply photo step failed to render', error);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="rounded-xl border border-danger-500/30 bg-danger-500/10 p-4 text-sm text-danger-500">
          Photos had trouble loading. The rest of the supply form is still safe; save the supply and retry the photo from the Media Library.
        </div>
      );
    }
    return this.props.children;
  }
}

export function EditSupplyModal({ isOpen, onClose, onSuccess, supply }: EditSupplyModalProps) {
  const isEditMode = !!supply;
  const { supplyFormMode, setSupplyFormMode } = usePreferencesStore();
  const [formMode, setFormMode] = useState<SupplyFormMode>(supplyFormMode);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeStep, setActiveStep] = useState<SupplyFormStep>('identity');
  const { success: showSuccess, error: showError } = useToast();

  const [supplyClass, setSupplyClass] = useState<SupplyClass | ''>('');
  const [supplyType, setSupplyType] = useState<SupplyType | ''>('');
  const [name, setName] = useState('');
  const [showNameSuggestions, setShowNameSuggestions] = useState(false);
  const [description, setDescription] = useState('');
  const [notes, setNotes] = useState('');
  const [sku, setSku] = useState('');
  const [manufacturerSku, setManufacturerSku] = useState('');
  const [manufacturerSku2, setManufacturerSku2] = useState('');
  const [manufacturerSku3, setManufacturerSku3] = useState('');
  const [unitOfMeasure, setUnitOfMeasure] = useState('piece');
  const [quantityInStock, setQuantityInStock] = useState('0');
  const [costPerUnit, setCostPerUnit] = useState('0');
  const [reorderLevel, setReorderLevel] = useState('0');
  const [reorderQuantity, setReorderQuantity] = useState('');
  const [supplier, setSupplier] = useState('');
  const [supplierId, setSupplierId] = useState('');
  const [supplierSku, setSupplierSku] = useState('');
  const [purchaseUrl, setPurchaseUrl] = useState('');
  const [storageLocationId, setStorageLocationId] = useState('');
  const [storageNotes, setStorageNotes] = useState('');
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [isActive, setIsActive] = useState(true);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);

  const [materialType, setMaterialType] = useState('');
  const [finish, setFinish] = useState('');
  const [surfaceTreatments, setSurfaceTreatments] = useState<string[]>([]);
  const [colorSummary, setColorSummary] = useState('');
  const [colorId, setColorId] = useState('');
  const [brandId, setBrandId] = useState('');
  const [productionMethodIds, setProductionMethodIds] = useState<string[]>([]);
  const [compatibleItemTypeIds, setCompatibleItemTypeIds] = useState<string[]>([]);
  const [compatibleMachineIds, setCompatibleMachineIds] = useState<string[]>([]);

  const [dimensionHeight, setDimensionHeight] = useState('');
  const [dimensionWidth, setDimensionWidth] = useState('');
  const [dimensionDepth, setDimensionDepth] = useState('');
  const [dimensionUnit, setDimensionUnit] = useState('in');
  const [weight, setWeight] = useState('');
  const [weightOunces, setWeightOunces] = useState('');
  const [weightUnit, setWeightUnit] = useState('oz');
  const [blankShape, setBlankShape] = useState('');
  const [blankNotes, setBlankNotes] = useState('');
  const [printableAreaWidth, setPrintableAreaWidth] = useState('');
  const [printableAreaHeight, setPrintableAreaHeight] = useState('');
  const [engravableAreaWidth, setEngravableAreaWidth] = useState('');
  const [engravableAreaHeight, setEngravableAreaHeight] = useState('');
  const [isDoubleSided, setIsDoubleSided] = useState(false);
  const [isCurvedSurface, setIsCurvedSurface] = useState(false);
  const [apparelSize, setApparelSize] = useState('');
  const [filamentMaker, setFilamentMaker] = useState('');
  const [filamentType, setFilamentType] = useState('');
  const [filamentDiameter, setFilamentDiameter] = useState('');
  const [filamentNetWeight, setFilamentNetWeight] = useState('');
  const [hardwareType, setHardwareType] = useState('');
  const [hardwareOuterDiameter, setHardwareOuterDiameter] = useState('');
  const [hardwareInnerDiameter, setHardwareInnerDiameter] = useState('');
  const [hardwareWireThickness, setHardwareWireThickness] = useState('');
  const [hardwareOpeningStyle, setHardwareOpeningStyle] = useState('');
  const [customShapes, setCustomShapes] = useState<string[]>([]);
  const [tumblerProfileId, setTumblerProfileId] = useState('');
  const [tumblerSizeLabel, setTumblerSizeLabel] = useState('');
  const [measurementSystem, setMeasurementSystem] = useState<'american' | 'metric'>('american');

  const [suppliers, setSuppliers] = useState<RefSupplier[]>([]);
  const [colors, setColors] = useState<RefColor[]>([]);

  const [quickAddType, setQuickAddType] = useState<'supplier' | 'color' | 'brand' | 'location' | null>(null);
  const [referenceEditTarget, setReferenceEditTarget] = useState<ReferenceEditTarget>(null);

  const effectiveSupplyClass = supplyClass || 'OTHER';
  const effectiveSupplyType = supplyType || 'OTHER';
  const selectedNameTemplate = SUPPLY_NAME_TEMPLATES.find(
    (template) => template.name.toLowerCase() === name.trim().toLowerCase()
  );

  const steps = useMemo(
    () => visibleSteps(effectiveSupplyClass).filter((step) => surfaceTreatments.length > 0 ? step !== 'productionMethods' : true),
    [effectiveSupplyClass, surfaceTreatments.length]
  );
  const stepIndex = steps.indexOf(activeStep);

  const { data: productionMethods } = useQuery({
    queryKey: ['production-methods'],
    queryFn: () => productionMethodsApi.getAll(false),
    enabled: isOpen,
  });
  const { data: itemTypes } = useQuery({
    queryKey: ['item-types'],
    queryFn: () => itemTypesApi.getAll(false),
    enabled: isOpen,
  });
  const { data: machines } = useQuery({
    queryKey: ['machines'],
    queryFn: () => machinesApi.getAll(false),
    enabled: isOpen,
  });
  const { data: storageLocationTree, refetch: refetchStorageLocations } = useQuery({
    queryKey: ['storage-locations-tree'],
    queryFn: () => storageLocationsApi.getTree(),
    enabled: isOpen,
  });
  const { data: supplyLookups = [], refetch: refetchSupplyLookups } = useQuery({
    queryKey: ['supply-lookups'],
    queryFn: () => referenceDataApi.getSupplyLookups(),
    enabled: isOpen,
  });

  const unitOptions = useMemo(
    () => supplyClass && supplyType
      ? UNITS_OF_MEASURE.filter((u) => allowedUnitsFor(supplyClass, supplyType).includes(u.value))
      : [],
    [supplyClass, supplyType]
  );
  const storageLocationOptions = useMemo(
    () => flattenStorageLocations(storageLocationTree ?? [])
      .map((location) => ({ ...location, label: displayStorageLocationLabel(location.label) }))
      .sort((a, b) => a.label.localeCompare(b.label)),
    [storageLocationTree]
  );
  const supplierOptions = useMemo(() => {
    const sorted = sortSuppliersForEntry(suppliers);
    const selected = suppliers.find((item) => item.id === supplierId);
    return selected && !sorted.some((item) => item.id === selected.id) ? [selected, ...sorted] : sorted;
  }, [supplierId, suppliers]);

  const relevantProductionMethods = useMemo(
    () =>
      (productionMethods ?? []).filter((method) =>
        supplyClass && supplyType && productionMethodMatchesSupplyClass(method.supplyTypes, supplyClass, supplyType)
      ),
    [productionMethods, supplyClass, supplyType]
  );

  const relevantMachines = useMemo(() => {
    if (!machines) return [];
    if (productionMethodIds.length === 0) return [];
    return machines.filter(
      (m) => !m.productionMethodId || productionMethodIds.includes(m.productionMethodId)
    );
  }, [machines, productionMethodIds]);

  const isFilamentSupply = supplyClass === 'CONSUMABLE' && supplyType === 'FILAMENT';
  const isApparelSupply = supplyClass === 'BLANK_APPAREL';
  const isHardwareSupply = supplyClass === 'HARDWARE_COMPONENT';
  const showMaterialControls = !!supplyClass && (supplyClass !== 'CONSUMABLE' || isFilamentSupply);
  const showPhysicalDimensions = !!supplyClass && supplyClass !== 'CONSUMABLE';
  const showTumblerBlankGuide = supplyClass === 'CUSTOMIZABLE_BLANK' && supplyType === 'TUMBLER_BLANK';
  const showPhysicalCharacteristics = supplyClass === 'CUSTOMIZABLE_BLANK' || supplyClass === 'RAW_MATERIAL';
  const lookupOptions = useMemo(
    () => ({
      blankMaterials: lookupLabels(supplyLookups, 'BLANK_MATERIAL'),
      filamentMaterials: lookupLabels(supplyLookups, 'FILAMENT_MATERIAL'),
      filamentFinishes: lookupLabels(supplyLookups, 'FILAMENT_FINISH'),
      filamentSpoolWeights: lookupLabels(supplyLookups, 'FILAMENT_SPOOL_WEIGHT'),
      hardwareMaterials: lookupLabels(supplyLookups, 'HARDWARE_MATERIAL'),
      hardwareFinishes: lookupLabels(supplyLookups, 'HARDWARE_FINISH'),
      hardwareFinishColors: lookupLabels(supplyLookups, 'HARDWARE_FINISH_COLOR'),
    }),
    [supplyLookups]
  );
  const materialTypeOptions = useMemo(
    () => {
      if (isFilamentSupply) return withLookupFallback(lookupOptions.filamentMaterials, FILAMENT_MATERIAL_TYPES);
      if (isApparelSupply) return APPAREL_MATERIAL_TYPES;
      if (isHardwareSupply) return withLookupFallback(lookupOptions.hardwareMaterials, HARDWARE_MATERIAL_TYPES);
      if (supplyClass === 'CUSTOMIZABLE_BLANK') return withLookupFallback(lookupOptions.blankMaterials, BLANK_MATERIAL_TYPES);
      return [...MATERIAL_TYPES];
    },
    [isApparelSupply, isFilamentSupply, isHardwareSupply, lookupOptions.blankMaterials, lookupOptions.filamentMaterials, lookupOptions.hardwareMaterials, supplyClass]
  );
  const finishOptions = useMemo(
    () => {
      if (isFilamentSupply) return withLookupFallback(lookupOptions.filamentFinishes, FILAMENT_FINISH_OPTIONS);
      if (isHardwareSupply) return withLookupFallback(lookupOptions.hardwareFinishes, HARDWARE_FINISH_OPTIONS);
      return FINISH_OPTIONS_BY_MATERIAL[materialType] ?? [];
    },
    [isFilamentSupply, isHardwareSupply, lookupOptions.filamentFinishes, lookupOptions.hardwareFinishes, materialType]
  );
  const surfaceTreatmentOptions = useMemo(() => {
    if (isApparelSupply) return APPAREL_SURFACE_OPTIONS;
    if (SURFACE_OPTIONS_BY_MATERIAL[materialType]) return SURFACE_OPTIONS_BY_MATERIAL[materialType] ?? [];
    const materialDefaults = MATERIAL_DEFAULTS[materialType]?.treatments ?? [];
    return materialDefaults;
  }, [isApparelSupply, materialType]);
  const showFinishControl = showMaterialControls && !isApparelSupply && finishOptions.length > 0;
  const showCoatingSurface = showMaterialControls && !isFilamentSupply && !!materialType && surfaceTreatmentOptions.length > 0;
  const shapeLookups = useMemo(
    () => supplyLookups.filter((lookup) => lookup.category === 'SHAPE'),
    [supplyLookups]
  );
  const hardwareTypeLookups = useMemo(
    () => supplyLookups.filter((lookup) => lookup.category === 'HARDWARE_TYPE'),
    [supplyLookups]
  );
  const shapeOptions = useMemo(
    () => Array.from(new Set([...SHAPE_OPTIONS, ...shapeLookups.map((lookup) => lookup.label), ...customShapes])).sort((a, b) => a.localeCompare(b)),
    [customShapes, shapeLookups]
  );
  const hardwareTypeOptions = useMemo(
    () => Array.from(new Set([...hardwareTypeLookups.map((lookup) => lookup.label), ...HARDWARE_TYPES])).sort((a, b) => a.localeCompare(b)),
    [hardwareTypeLookups]
  );
  const selectedHardwareLookup = useMemo(
    () => hardwareTypeLookups.find((lookup) => lookup.label === hardwareType || lookup.value === hardwareType),
    [hardwareType, hardwareTypeLookups]
  );
  const hardwareLookupMetadata = useMemo(
    () => parseHardwareLookupMetadata(selectedHardwareLookup?.metadataJson),
    [selectedHardwareLookup]
  );
  const hardwareDimensionFields = useMemo(
    () => hardwareLookupMetadata.fields ?? HARDWARE_FIELDS_BY_TYPE[hardwareType] ?? ['outerDiameter', 'innerDiameter', 'wireThickness'],
    [hardwareLookupMetadata.fields, hardwareType]
  );
  const hardwareOpeningStyleOptions = useMemo(
    () => hardwareLookupMetadata.openingStyles ?? [],
    [hardwareLookupMetadata.openingStyles]
  );
  const hardwareFinishColorOptions = useMemo(
    () => withLookupFallback(lookupOptions.hardwareFinishColors, HARDWARE_FINISH_COLOR_OPTIONS),
    [lookupOptions.hardwareFinishColors]
  );
  const filamentNetWeightOptions = useMemo(
    () => withLookupFallback(lookupOptions.filamentSpoolWeights, FILAMENT_NET_WEIGHTS),
    [lookupOptions.filamentSpoolWeights]
  );
  const primaryColorOptions = useMemo(
    () => {
      const colorSortOrder = new Map(colors.map((color) => [color.name, color.sortOrder ?? colorRank(color.name)]));
      return Array.from(new Set([...COLOR_OPTIONS, ...colors.map((color) => color.name)]))
        .sort((a, b) => (colorSortOrder.get(a) ?? colorRank(a)) - (colorSortOrder.get(b) ?? colorRank(b)) || a.localeCompare(b));
    },
    [colors]
  );
  const colorHexByName = useMemo(
    () => new Map(colors.map((color) => [color.name, color.hexCode])),
    [colors]
  );
  const selectedColors = useMemo(
    () => colorSummary ? colorSummary.split(' / ').filter(Boolean) : [],
    [colorSummary]
  );
  const visiblePrimaryColorOptions = useMemo(
    () => [
      ...selectedColors,
      ...primaryColorOptions.filter((color) => !selectedColors.includes(color)),
    ],
    [primaryColorOptions, selectedColors]
  );
  const standardColorOptions = useMemo(
    () => visiblePrimaryColorOptions.filter((color) => !isMetallicColor(color)),
    [visiblePrimaryColorOptions]
  );
  const metallicColorOptions = useMemo(
    () => visiblePrimaryColorOptions.filter((color) => isMetallicColor(color)),
    [visiblePrimaryColorOptions]
  );
  const filteredNameTemplates = useMemo(() => {
    const query = name.trim().toLowerCase();
    if (!query) return SUPPLY_NAME_TEMPLATES;
    return SUPPLY_NAME_TEMPLATES.filter((template) =>
      template.name.toLowerCase().startsWith(query)
    );
  }, [name]);

  const selectedTumblerProfile = TUMBLER_BLANK_PROFILES.find((profile) => profile.id === tumblerProfileId);

  const dimensionConversions = useMemo(() => {
    const toInches = (value: number) => {
      if (dimensionUnit === 'mm') return value / 25.4;
      if (dimensionUnit === 'cm') return value / 2.54;
      return value;
    };
    const toCentimeters = (value: number) => {
      if (dimensionUnit === 'mm') return value / 10;
      if (dimensionUnit === 'in') return value * 2.54;
      return value;
    };
    const convert = (rawValue: string, label: string) => {
      const value = parseFloat(rawValue);
      if (!Number.isFinite(value) || value <= 0) return null;
      if (measurementSystem === 'american') {
        const inches = toInches(value);
        const feet = Math.floor(inches / 12);
        const remainingInches = inches - feet * 12;
        return `${label}: ${inches.toFixed(2)} in (${feet} ft ${remainingInches.toFixed(2)} in)`;
      }
      const centimeters = toCentimeters(value);
      return `${label}: ${centimeters.toFixed(2)} cm (${(centimeters * 10).toFixed(1)} mm)`;
    };

    return [
      convert(dimensionHeight, 'Height'),
      convert(dimensionWidth, 'Width'),
      convert(dimensionDepth, 'Depth'),
    ].filter(Boolean);
  }, [dimensionDepth, dimensionHeight, dimensionUnit, dimensionWidth, measurementSystem]);
  const weightConversions = useMemo(() => {
    const pounds = parseFloat(weight) || 0;
    const ounces = parseFloat(weightOunces) || 0;
    if (measurementSystem === 'american') {
      const totalOunces = pounds * 16 + ounces;
      if (totalOunces <= 0) return [];
      return [`Weight: ${Math.floor(totalOunces / 16)} lb ${(totalOunces % 16).toFixed(2)} oz`, `${totalOunces.toFixed(2)} oz total`];
    }
    const grams = parseFloat(weight);
    if (!Number.isFinite(grams) || grams <= 0) return [];
    return [`Weight: ${grams.toFixed(1)} g`, `${(grams / 1000).toFixed(3)} kg`];
  }, [measurementSystem, weight, weightOunces]);

  useEffect(() => {
    if (!isOpen) return;
    setFormMode(supplyFormMode);
    window.setTimeout(() => nameInputRef.current?.focus(), 0);
  }, [isOpen, supplyFormMode]);

  useEffect(() => {
    if (!unitOptions.length) return;
    if (!unitOptions.some((u) => u.value === unitOfMeasure)) {
      setUnitOfMeasure(unitOptions[0]?.value ?? 'piece');
    }
  }, [unitOptions, unitOfMeasure]);

  useEffect(() => {
    if (!isOpen) return;
    void Promise.all([
      referenceDataApi.getSuppliers(),
      referenceDataApi.getColors(),
    ]).then(([s, c]) => {
      setSuppliers(s);
      setColors(c);
    });
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || isEditMode || supplierId || !supplierOptions.length) return;
    const temu = supplierOptions.find((item) => item.name.toLowerCase() === 'temu');
    if (temu) {
      setSupplierId(temu.id);
      setSupplier(temu.name);
    }
  }, [isEditMode, isOpen, supplierId, supplierOptions]);

  useEffect(() => {
    if (!materialType) return;
    const defaults = MATERIAL_DEFAULTS[materialType];
    if (!defaults) return;
    setFinish((current) => current || defaults.finish);
  }, [materialType]);

  useEffect(() => {
    if (!isOpen) return;
    if (supply) {
      const s = supply as unknown as Supply;
      const cls = s.supplyClass ?? supplyClassFromType(s.supplyType ?? 'OTHER');
      setSupplyClass(cls);
      setSupplyType(s.supplyType ?? 'OTHER');
      setName(s.name || '');
      setDescription(s.description || '');
      setNotes(s.notes || '');
      setSku(s.sku || '');
      setManufacturerSku(s.manufacturerSku || '');
      setManufacturerSku2(s.manufacturerSku2 || '');
      setManufacturerSku3(s.manufacturerSku3 || '');
      setUnitOfMeasure(s.unitOfMeasure || 'piece');
      setQuantityInStock(String(s.quantityInStock ?? 0));
      setCostPerUnit(String(s.costPerUnit ?? 0));
      setReorderLevel(String(s.reorderLevel ?? 0));
      setReorderQuantity(s.reorderQuantity != null ? String(s.reorderQuantity) : '');
      setSupplier(s.supplier || '');
      setSupplierId(s.supplierId || '');
      setSupplierSku(s.supplierSku || '');
      setPurchaseUrl(s.purchaseUrl || '');
      setStorageLocationId(s.storageLocationId || '');
      setStorageNotes(s.storageNotes || '');
      setImageUrl(s.imageUrl || null);
      setIsActive(s.isActive ?? true);
      setMaterialType(s.materialType || '');
      setFinish(s.finish || '');
      setColorId(s.colorId || '');
      setBrandId(s.brandId || '');
      setProductionMethodIds(s.productionMethodIds ?? []);
      setCompatibleItemTypeIds(s.compatibleItemTypeIds ?? []);
      setCompatibleMachineIds(s.compatibleMachineIds ?? []);
      setDimensionHeight(s.dimensionHeight != null ? String(s.dimensionHeight) : '');
      setDimensionWidth(s.dimensionWidth != null ? String(s.dimensionWidth) : '');
      setDimensionDepth(s.dimensionDepth != null ? String(s.dimensionDepth) : '');
      setDimensionUnit(s.dimensionUnit || 'in');
      setMeasurementSystem((s.dimensionUnit || 'in') === 'in' || s.weightUnit === 'lb' || s.weightUnit === 'oz' ? 'american' : 'metric');
      if (s.weight != null && (s.weightUnit === 'lb' || s.weightUnit === 'oz')) {
        const totalOunces = s.weightUnit === 'oz' ? s.weight : s.weight * 16;
        setWeight(String(Math.floor(totalOunces / 16)));
        setWeightOunces(String(Number((totalOunces % 16).toFixed(2))));
        setWeightUnit('lb');
      } else {
        setWeight(s.weight != null ? String(s.weight) : '');
        setWeightOunces('');
        setWeightUnit(s.weightUnit || 'g');
      }
      setBlankShape(s.blankShape || '');
      setBlankNotes(s.blankNotes || '');
      setPrintableAreaWidth(s.printableAreaWidth != null ? String(s.printableAreaWidth) : '');
      setPrintableAreaHeight(s.printableAreaHeight != null ? String(s.printableAreaHeight) : '');
      setEngravableAreaWidth(s.engravableAreaWidth != null ? String(s.engravableAreaWidth) : '');
      setEngravableAreaHeight(s.engravableAreaHeight != null ? String(s.engravableAreaHeight) : '');
      setIsDoubleSided(s.isDoubleSided ?? false);
      setIsCurvedSurface(s.isCurvedSurface ?? false);
      setColorSummary(s.colorSummary || '');
      setSurfaceTreatments(s.coating ? s.coating.split(',').map((value) => value.trim()).filter(Boolean) : []);
      setApparelSize(s.apparelSize || '');
      setFilamentMaker(s.filamentMaker || '');
      setFilamentType(s.filamentType || '');
      setFilamentDiameter(s.filamentDiameter || '');
      setFilamentNetWeight(s.filamentNetWeight || '');
      setHardwareType(s.hardwareType || '');
      setHardwareOuterDiameter(s.hardwareOuterDiameter != null ? String(s.hardwareOuterDiameter) : '');
      setHardwareInnerDiameter(s.hardwareInnerDiameter != null ? String(s.hardwareInnerDiameter) : '');
      setHardwareWireThickness(s.hardwareWireThickness != null ? String(s.hardwareWireThickness) : '');
      setHardwareOpeningStyle(s.hardwareOpeningStyle || '');
      setTumblerProfileId('');
      setTumblerSizeLabel('');
      setActiveStep('identity');
    } else {
      resetForm();
    }
    setError('');
  }, [supply, isOpen]);

  useEffect(() => {
    if (finish && !finishOptions.includes(finish)) {
      setFinish('');
    }
  }, [finish, finishOptions]);

  useEffect(() => {
    if (hardwareOpeningStyle && !hardwareOpeningStyleOptions.includes(hardwareOpeningStyle)) {
      setHardwareOpeningStyle('');
    }
  }, [hardwareOpeningStyle, hardwareOpeningStyleOptions]);

  useEffect(() => {
    if (!showTumblerBlankGuide) {
      setTumblerProfileId('');
      setTumblerSizeLabel('');
    }
  }, [showTumblerBlankGuide]);

  useEffect(() => {
    if (!steps.includes(activeStep)) {
      setActiveStep(steps[0]);
    }
  }, [steps, activeStep]);

  useEffect(() => {
    if (showCoatingSurface) return;
    setSurfaceTreatments([]);
  }, [showCoatingSurface]);

  useEffect(() => {
    const selectedSurface = surfaceTreatments[0];
    if (!selectedSurface || !materialType || !productionMethods?.length) return;
    const terms = inferredProductionTerms(materialType, selectedSurface);
    if (!terms.length) return;
    const inferredMethods = productionMethods.filter((method) => {
      const haystack = `${method.name} ${method.code} ${method.description ?? ''}`.toLowerCase();
      return terms.some((term) => haystack.includes(term));
    });
    if (!inferredMethods.length) return;
    const inferredMethodIds = inferredMethods.map((method) => method.id);
    setProductionMethodIds(inferredMethodIds);
    setCompatibleMachineIds((machines ?? [])
      .filter((machine) => machine.productionMethodId && inferredMethodIds.includes(machine.productionMethodId))
      .map((machine) => machine.id));
  }, [machines, materialType, productionMethods, surfaceTreatments]);

  const resetForm = () => {
    setSupplyClass('');
    setSupplyType('');
    setName('');
    setShowNameSuggestions(false);
    setDescription('');
    setNotes('');
    setSku('');
    setManufacturerSku('');
    setManufacturerSku2('');
    setManufacturerSku3('');
    setUnitOfMeasure('piece');
    setQuantityInStock('0');
    setCostPerUnit('0');
    setReorderLevel('0');
    setReorderQuantity('');
    setSupplier('');
    setSupplierId('');
    setSupplierSku('');
    setPurchaseUrl('');
    setStorageLocationId('');
    setStorageNotes('');
    setImageUrl(null);
    setIsActive(true);
    setMaterialType('');
    setFinish('');
    setColorId('');
    setBrandId('');
    setProductionMethodIds([]);
    setCompatibleItemTypeIds([]);
    setCompatibleMachineIds([]);
    setDimensionHeight('');
    setDimensionWidth('');
    setDimensionDepth('');
    setDimensionUnit('in');
    setWeight('');
    setWeightOunces('');
    setWeightUnit('oz');
    setBlankShape('');
    setBlankNotes('');
    setPrintableAreaWidth('');
    setPrintableAreaHeight('');
    setEngravableAreaWidth('');
    setEngravableAreaHeight('');
    setIsDoubleSided(false);
    setIsCurvedSurface(false);
    setColorSummary('');
    setSurfaceTreatments([]);
    setApparelSize('');
    setFilamentMaker('');
    setFilamentType('');
    setFilamentDiameter('');
    setFilamentNetWeight('');
    setHardwareType('');
    setHardwareOuterDiameter('');
    setHardwareInnerDiameter('');
    setHardwareWireThickness('');
    setHardwareOpeningStyle('');
    setTumblerProfileId('');
    setTumblerSizeLabel('');
    setMeasurementSystem('american');
    setActiveStep('identity');
  };

  const handleModeChange = (mode: SupplyFormMode) => {
    setFormMode(mode);
    setSupplyFormMode(mode);
  };

  const handleClassChange = (cls: SupplyClass) => {
    setSupplyClass(cls);
    const options = CRAFT_TYPES_BY_CLASS[cls];
    const nextType = options[0]?.value ?? '';
    setSupplyType(nextType);
    if (nextType) {
      setUnitOfMeasure(defaultUnitForClass(cls, nextType));
      setBlankShape((current) => current || SHAPE_BY_SUPPLY_TYPE[nextType] || '');
    }
  };

  const handleClassSelectChange = (value: string) => {
    if (!value) {
      setSupplyClass('');
      setSupplyType('');
      return;
    }
    handleClassChange(value as SupplyClass);
  };

  const applyTumblerProfile = (profileId: string, sizeLabel?: string) => {
    const profile = TUMBLER_BLANK_PROFILES.find((item) => item.id === profileId);
    if (!profile) return;
    const size = profile.sizes.find((item) => item.label === sizeLabel) ?? profile.sizes[0];
    setTumblerProfileId(profile.id);
    setTumblerSizeLabel(size.label);
    setMaterialType(profile.materialType);
    setFinish(profile.finish);
    setDimensionUnit('in');
    setDimensionHeight(size.height);
    setDimensionWidth(size.width);
    setDimensionDepth(size.depth);
  };

  const handleMaterialTypeChange = (nextMaterial: string) => {
    setMaterialType(nextMaterial);
    const defaults = MATERIAL_DEFAULTS[nextMaterial];
    if (defaults) {
      setFinish(isApparelSupply ? '' : defaults.finish);
      setSurfaceTreatments([]);
    } else {
      setFinish('');
      setSurfaceTreatments([]);
    }
  };

  const handleSupplySubtypeChange = (next: SupplyType) => {
    setSupplyType(next);
    if (supplyClass) {
      setUnitOfMeasure(defaultUnitForClass(supplyClass, next));
    }
    setBlankShape((current) => current || SHAPE_BY_SUPPLY_TYPE[next] || '');
    if (next === 'TUMBLER_BLANK') {
      applyTumblerProfile(TUMBLER_BLANK_PROFILES[0].id);
    }
  };

  const applyNameTemplate = (template: SupplyNameTemplate) => {
    setName(template.name);
    setSupplyClass(template.supplyClass);
    setSupplyType(template.supplyType);
    setUnitOfMeasure(defaultUnitForClass(template.supplyClass, template.supplyType));
    setBlankShape((current) => current || SHAPE_BY_SUPPLY_TYPE[template.supplyType] || '');
    if (template.supplyClass === 'BLANK_APPAREL') {
      setFinish('');
      setBlankShape('');
      setIsDoubleSided(false);
      setIsCurvedSurface(false);
    }
    setShowNameSuggestions(false);
  };

  const handleNameChange = (nextName: string) => {
    setName(nextName);
    setShowNameSuggestions(true);
    const inferredTemplate = inferTemplateFromName(nextName);
    if (inferredTemplate) {
      setSupplyClass(inferredTemplate.supplyClass);
      setSupplyType(inferredTemplate.supplyType);
      setUnitOfMeasure(defaultUnitForClass(inferredTemplate.supplyClass, inferredTemplate.supplyType));
      if (inferredTemplate.supplyClass === 'BLANK_APPAREL') {
        setFinish('');
        setBlankShape('');
        setIsDoubleSided(false);
        setIsCurvedSurface(false);
      } else {
        setBlankShape((current) => current || SHAPE_BY_SUPPLY_TYPE[inferredTemplate.supplyType] || '');
      }
      return;
    }
    setSupplyClass('');
    setSupplyType('');
  };

  const addCustomSupplyName = () => {
    setShowNameSuggestions(false);
    setSupplyClass('');
    setSupplyType('');
    window.setTimeout(() => {
      const typeSelect = document.getElementById('supply-class-select');
      typeSelect?.focus();
    }, 0);
  };

  const toggleId = (ids: string[], id: string, setter: (v: string[]) => void) => {
    setter(ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]);
  };

  const toggleColor = (color: string) => {
    const values = colorSummary ? colorSummary.split(' / ').filter(Boolean) : [];
    const next = values.includes(color) ? values.filter((value) => value !== color) : [...values, color];
    setColorSummary(next.join(' / '));
  };

  const validateStep = (step: SupplyFormStep): boolean => {
    if (step === 'identity' && !name.trim()) {
      setError('Supply name is required');
      return false;
    }
    if (step === 'identity' && (!supplyClass || !supplyType)) {
      setError('Select a Product & Supply Type and Supply Subtype');
      return false;
    }
    setError('');
    return true;
  };

  const normalizedWeightValue = () => {
    if (!showPhysicalDimensions) return null;
    if (measurementSystem === 'american') {
      const pounds = parseFloat(weight) || 0;
      const ounces = parseFloat(weightOunces) || 0;
      const totalPounds = pounds + ounces / 16;
      return totalPounds > 0 ? totalPounds : null;
    }
    const grams = parseFloat(weight);
    return Number.isFinite(grams) && grams > 0 ? grams : null;
  };

  const buildPayload = () => ({
    name: name.trim(),
    description: description.trim(),
    notes: notes.trim() || null,
    supplyType: effectiveSupplyType,
    supplyClass: effectiveSupplyClass,
    sku: sku.trim() || null,
    manufacturerSku: manufacturerSku.trim() || null,
    manufacturerSku2: manufacturerSku2.trim() || null,
    manufacturerSku3: manufacturerSku3.trim() || null,
    unitOfMeasure: unitOfMeasure.trim() || 'piece',
    quantityInStock: parseFloat(quantityInStock) || 0,
    costPerUnit: parseFloat(costPerUnit) || 0,
    reorderLevel: parseFloat(reorderLevel) || 0,
    reorderQuantity: reorderQuantity ? parseFloat(reorderQuantity) : null,
    supplier: supplier.trim() || null,
    supplierId: supplierId || null,
    supplierSku: supplierSku.trim() || null,
    purchaseUrl: purchaseUrl.trim() || null,
    storageLocationId: storageLocationId || null,
    storageNotes: storageNotes.trim() || null,
    imageUrl,
    isActive,
    materialType: showMaterialControls ? materialType || null : null,
    finish: showFinishControl ? finish || null : null,
    coating: showCoatingSurface ? surfaceTreatments.join(', ') || null : null,
    colorSummary: colorSummary || null,
    weight: normalizedWeightValue(),
    weightUnit: showPhysicalDimensions ? (measurementSystem === 'american' ? 'lb' : 'g') : null,
    apparelSize: isApparelSupply ? apparelSize || null : null,
    filamentMaker: filamentMaker || null,
    filamentType: filamentType || null,
    filamentDiameter: filamentDiameter || null,
    filamentNetWeight: filamentNetWeight || null,
    hardwareType: isHardwareSupply ? hardwareType || null : null,
    hardwareOuterDiameter: isHardwareSupply && hardwareDimensionFields.includes('outerDiameter') && hardwareOuterDiameter ? parseFloat(hardwareOuterDiameter) : null,
    hardwareInnerDiameter: isHardwareSupply && hardwareDimensionFields.includes('innerDiameter') && hardwareInnerDiameter ? parseFloat(hardwareInnerDiameter) : null,
    hardwareWireThickness: isHardwareSupply && hardwareDimensionFields.includes('wireThickness') && hardwareWireThickness ? parseFloat(hardwareWireThickness) : null,
    hardwareOpeningStyle: isHardwareSupply && hardwareOpeningStyleOptions.length > 0 ? hardwareOpeningStyle || null : null,
    blankShape: showPhysicalCharacteristics ? blankShape || null : null,
    colorId: colorId || null,
    brandId: brandId || null,
    productionMethodIds,
    compatibleItemTypeIds,
    compatibleMachineIds,
    dimensionHeight: showPhysicalDimensions && dimensionHeight ? parseFloat(dimensionHeight) : null,
    dimensionWidth: showPhysicalDimensions && dimensionWidth ? parseFloat(dimensionWidth) : null,
    dimensionDepth: showPhysicalDimensions && dimensionDepth ? parseFloat(dimensionDepth) : null,
    dimensionUnit: showPhysicalDimensions ? dimensionUnit || 'in' : null,
    isBlank: isBlankSupplyClass(effectiveSupplyClass),
    blankNotes: blankNotes.trim() || null,
    printableAreaWidth: printableAreaWidth ? parseFloat(printableAreaWidth) : null,
    printableAreaHeight: printableAreaHeight ? parseFloat(printableAreaHeight) : null,
    engravableAreaWidth: engravableAreaWidth ? parseFloat(engravableAreaWidth) : null,
    engravableAreaHeight: engravableAreaHeight ? parseFloat(engravableAreaHeight) : null,
    isDoubleSided: showPhysicalCharacteristics ? isDoubleSided : false,
    isCurvedSurface: showPhysicalCharacteristics ? isCurvedSurface : false,
    category: null,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateStep('identity')) {
      setActiveStep('identity');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const requestData = buildPayload();
      let savedSupplyId: string | undefined;
      if (isEditMode && supply) {
        const supplyId = (supply as { id?: string }).id;
        if (!supplyId) return;
        const response = await client.put(`/supplies/${supplyId}`, requestData);
        savedSupplyId = (response.data as { id?: string }).id || supplyId;
        showSuccess('Supply updated successfully');
      } else {
        const response = await client.post('/supplies', requestData);
        savedSupplyId = (response.data as { id?: string }).id;
        showSuccess('Supply created successfully');
      }
      if (savedSupplyId && requestData.imageUrl) {
        try {
          await uploadApi.finalizeMedia({
            url: requestData.imageUrl,
            entityType: 'SUPPLY',
            entityId: savedSupplyId,
            displayName: requestData.name,
            primary: true,
          });
        } catch (renameError) {
          console.warn('Supply photo saved but could not be finalized', renameError);
          showError('Supply saved, but the photo could not be renamed yet. It remains in the Media Library.');
        }
      }
      onSuccess();
      onClose();
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
        || (err instanceof Error ? err.message : 'Failed to save supply');
      setError(message);
      showError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleQuickAddSupplier = async (supplierName: string, websiteUrl?: string, sortOrderValue?: string) => {
    const created = await referenceDataApi.createSupplier({
      name: supplierName,
      websiteUrl,
      sortOrder: sortOrderValue ? Number.parseInt(sortOrderValue, 10) || 0 : 0,
    });
    setSuppliers((prev) => sortSuppliersForEntry([...prev, created]));
    setSupplierId(created.id);
    setSupplier(created.name);
  };

  const handleQuickAddColor = async (colorName: string, hexCode?: string, sortOrderValue?: string) => {
    const created = await referenceDataApi.createColor({
      name: colorName,
      hexCode: hexCode || '#808080',
      sortOrder: sortOrderValue ? Number.parseInt(sortOrderValue, 10) || 0 : colorRank(colorName),
    });
    setColors((prev) => [...prev, created].sort((a, b) => (a.sortOrder ?? colorRank(a.name)) - (b.sortOrder ?? colorRank(b.name)) || a.name.localeCompare(b.name)));
    setColorId(created.id);
    const selected = colorSummary ? colorSummary.split(' / ').filter(Boolean) : [];
    if (!selected.includes(created.name)) {
      setColorSummary([...selected, created.name].join(' / '));
    }
  };

  const handleQuickAddLocation = async (locationName: string, sortOrderValue?: string) => {
    const parent = storageLocationOptions.find((location) => location.id === storageLocationId);
    const created = await storageLocationsApi.create({
      name: locationName,
      locationType: parent ? 'BIN' : 'ROOM',
      parentId: parent?.id ?? null,
      sortOrder: sortOrderValue ? Number.parseInt(sortOrderValue, 10) || 0 : 0,
    });
    await refetchStorageLocations();
    setStorageLocationId(created.id);
  };

  const handleSaveReferenceEdit = async (name: string, extra?: string, secondExtra?: string) => {
    if (!referenceEditTarget?.item) return;
    if (referenceEditTarget.type === 'color') {
      const updated = await referenceDataApi.updateColor(referenceEditTarget.item.id, {
        name,
        hexCode: extra || referenceEditTarget.item.hexCode || '#808080',
        sortOrder: secondExtra ? Number.parseInt(secondExtra, 10) || 0 : referenceEditTarget.item.sortOrder ?? colorRank(name),
      });
      setColors((prev) => prev.map((color) => color.id === updated.id ? updated : color).sort((a, b) => (a.sortOrder ?? colorRank(a.name)) - (b.sortOrder ?? colorRank(b.name)) || a.name.localeCompare(b.name)));
      if (colorSummary.split(' / ').includes(referenceEditTarget.item.name)) {
        setColorSummary(colorSummary.split(' / ').map((color) => color === referenceEditTarget.item?.name ? updated.name : color).join(' / '));
      }
    } else if (referenceEditTarget.type === 'supplier') {
      const updated = await referenceDataApi.updateSupplier(referenceEditTarget.item.id, {
        name,
        websiteUrl: extra,
        sortOrder: secondExtra ? Number.parseInt(secondExtra, 10) || 0 : referenceEditTarget.item.sortOrder ?? 100,
      });
      setSuppliers((prev) => sortSuppliersForEntry(prev.map((supplier) => supplier.id === updated.id ? updated : supplier)));
      if (supplierId === updated.id) setSupplier(updated.name);
    } else {
      await storageLocationsApi.update(referenceEditTarget.item.id, {
        name,
        description: '',
        locationType: referenceEditTarget.item.locationType,
        parentId: referenceEditTarget.item.parentId,
        sortOrder: extra ? Number.parseInt(extra, 10) || 0 : referenceEditTarget.item.sortOrder,
      });
      await refetchStorageLocations();
    }
    setReferenceEditTarget(null);
  };

  const handleDeleteColor = async (color: string) => {
    const refColor = colors.find((item) => item.name === color);
    if (!refColor || !window.confirm(`Delete color "${color}"?`)) return;
    await referenceDataApi.deleteColor(refColor.id);
    setColors((prev) => prev.filter((item) => item.id !== refColor.id));
    setColorSummary(colorSummary.split(' / ').filter((item) => item !== color).join(' / '));
  };

  const handleDeleteSupplier = async () => {
    const selected = suppliers.find((item) => item.id === supplierId);
    if (!selected) return;
    const prioritySupplier = SUPPLIER_PRIORITY.some((name) => selected.name.toLowerCase().startsWith(name.toLowerCase()));
    if (!window.confirm(`You are about to delete supplier "${selected.name}". Are you sure?`)) return;
    if (prioritySupplier && window.prompt(`Type ${selected.name} to confirm deleting this default supplier.`) !== selected.name) return;
    await referenceDataApi.deleteSupplier(selected.id);
    setSuppliers((prev) => prev.filter((item) => item.id !== selected.id));
    setSupplierId('');
    setSupplier('');
  };

  const handleDeleteLocation = async () => {
    const selected = storageLocationOptions.find((item) => item.id === storageLocationId);
    if (!selected || !window.confirm(`You are about to delete location "${selected.label}". Are you sure?`)) return;
    await storageLocationsApi.delete(selected.id);
    await refetchStorageLocations();
    setStorageLocationId('');
  };

  const handleAddShape = async () => {
    const value = window.prompt('Add shape');
    const next = value?.trim();
    if (!next) return;
    try {
      const created = await referenceDataApi.createSupplyLookup({
        category: 'SHAPE',
        value: next,
        label: next,
        sortOrder: 500,
      });
      await refetchSupplyLookups();
      setBlankShape(created.label);
    } catch (err) {
      setCustomShapes((prev) => Array.from(new Set([...prev, next])).sort((a, b) => a.localeCompare(b)));
      setBlankShape(next);
      showError(err instanceof Error ? err.message : 'Shape was added for this session only');
    }
  };

  const handleEditShape = async () => {
    if (!blankShape) return;
    const value = window.prompt('Edit shape', blankShape);
    const next = value?.trim();
    if (!next || next === blankShape) return;
    const existing = shapeLookups.find((lookup) => lookup.label === blankShape || lookup.value === blankShape);
    try {
      if (existing) {
        const updated = await referenceDataApi.updateSupplyLookup(existing.id, {
          category: 'SHAPE',
          value: next,
          label: next,
          sortOrder: existing.sortOrder ?? 500,
        });
        await refetchSupplyLookups();
        setBlankShape(updated.label);
      } else {
        const created = await referenceDataApi.createSupplyLookup({
          category: 'SHAPE',
          value: next,
          label: next,
          sortOrder: 500,
        });
        await refetchSupplyLookups();
        setBlankShape(created.label);
      }
      setCustomShapes((prev) => prev.filter((shape) => shape !== blankShape));
    } catch (err) {
      setCustomShapes((prev) => Array.from(new Set([...prev.filter((shape) => shape !== blankShape), next])).sort((a, b) => a.localeCompare(b)));
      setBlankShape(next);
      showError(err instanceof Error ? err.message : 'Shape was edited for this session only');
    }
  };

  const handleDeleteShape = async () => {
    if (!blankShape || !window.confirm(`You are about to delete shape "${blankShape}". Are you sure?`)) return;
    const existing = shapeLookups.find((lookup) => lookup.label === blankShape || lookup.value === blankShape);
    if (existing) {
      try {
        await referenceDataApi.deleteSupplyLookup(existing.id);
        await refetchSupplyLookups();
      } catch (err) {
        showError(err instanceof Error ? err.message : 'Could not delete shape');
        return;
      }
    }
    setCustomShapes((prev) => prev.filter((shape) => shape !== blankShape));
    setBlankShape('');
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingImage(true);
    setError('');
    try {
      const response = await uploadApi.uploadSupplyImage(file);
      setImageUrl(normalizeImageUrl(response.fileUrl));
      showSuccess('Photo uploaded. It now appears in Photos.');
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
        || (err instanceof Error ? err.message : 'Upload failed');
      setError(message);
      showError(message);
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const renderColorButton = (color: string) => {
    const refColor = colors.find((item) => item.name === color);
    const selected = colorSummary.split(' / ').includes(color);
    return (
      <div
        key={color}
        className={`group flex items-center gap-1 rounded-xl border px-2 py-1.5 text-xs ${
          selected
            ? 'border-amber-450 bg-amber-450/15 text-amber-700 dark:text-amber-300'
            : 'border-gray-200 dark:border-charcoal-700 text-gray-600 dark:text-warm-sand'
        }`}
      >
        <button type="button" onClick={() => toggleColor(color)} className="flex min-w-0 flex-1 items-center gap-2 text-left">
          <span
            className="h-5 w-5 shrink-0 rounded-full border border-gray-300 dark:border-charcoal-600 shadow-inner"
            style={swatchStyle(colorHexByName.get(color) ?? color)}
            aria-hidden="true"
          />
          <span className="truncate">{color}</span>
        </button>
        {refColor && (
          <div className="flex shrink-0 opacity-80 transition-opacity group-hover:opacity-100">
            <button type="button" onClick={() => setReferenceEditTarget({ type: 'color', item: refColor })} className="rounded p-1 hover:bg-white/70 dark:hover:bg-charcoal-700" title={`Edit ${color}`}>
              <Edit2 className="h-3 w-3" />
            </button>
            <button type="button" onClick={() => void handleDeleteColor(color)} className="rounded p-1 hover:bg-danger-500/10 hover:text-danger-500" title={`Delete ${color}`}>
              <Trash2 className="h-3 w-3" />
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderSupplyPhotoUpload = () => (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-gray-900 dark:text-warm-cream">
        Supply Photos
      </label>
      {imageUrl ? (
        <div className="space-y-2">
          <p className="text-xs font-medium text-gray-600 dark:text-warm-sand">Photos</p>
          <div className="relative">
            <img
              src={normalizeImageUrl(imageUrl) ?? imageUrl}
              alt="Supply"
              onError={() => setImageUrl(null)}
              className="w-full h-48 object-cover rounded-lg border"
            />
            <button type="button" onClick={() => setImageUrl(null)} className="absolute top-2 right-2 p-2 bg-danger-500/80 text-white rounded-lg"><XIcon className="w-4 h-4" /></button>
          </div>
        </div>
      ) : (
        <div className="border-2 border-dashed rounded-lg p-8 text-center">
          <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp" onChange={handleImageUpload} className="hidden" />
          <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploadingImage} className="flex flex-col items-center gap-2 w-full">
            {uploadingImage ? <Loader2 className="w-8 h-8 animate-spin text-amber-450" /> : <Upload className="w-8 h-8 text-gray-400" />}
            <span className="text-sm text-gray-600 dark:text-warm-sand">Upload supply photo</span>
          </button>
        </div>
      )}
      <p className="text-xs text-gray-500 dark:text-warm-sand/70">
        Use a clear front, package, or label view so the supply can be identified later.
      </p>
    </div>
  );

  const renderPhysicalDimensionsSection = () => {
    if (!showPhysicalDimensions) return null;
    return (
      <section className="space-y-4 pt-4 border-t border-gray-200 dark:border-charcoal-700">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-warm-cream">Physical Dimensions</h3>
          <div className="inline-flex rounded-lg border border-gray-200 dark:border-charcoal-700 p-0.5 text-xs">
            <button
              type="button"
              onClick={() => {
                setMeasurementSystem('american');
                setDimensionUnit('in');
                setWeightUnit('lb');
              }}
              className={`rounded-md px-3 py-1.5 ${measurementSystem === 'american' ? 'bg-amber-450/15 text-amber-700 dark:text-amber-300' : 'text-gray-600 dark:text-warm-sand'}`}
            >
              American
            </button>
            <button
              type="button"
              onClick={() => {
                setMeasurementSystem('metric');
                setDimensionUnit('cm');
                setWeightUnit('g');
              }}
              className={`rounded-md px-3 py-1.5 ${measurementSystem === 'metric' ? 'bg-amber-450/15 text-amber-700 dark:text-amber-300' : 'text-gray-600 dark:text-warm-sand'}`}
            >
              Metric
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3">
          <div>
            <label className="block text-xs mb-1">Height ({dimensionUnit})</label>
            <input type="number" min="0" step="0.01" value={dimensionHeight} onChange={(e) => setDimensionHeight(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm" />
          </div>
          <div>
            <label className="block text-xs mb-1">Width ({dimensionUnit})</label>
            <input type="number" min="0" step="0.01" value={dimensionWidth} onChange={(e) => setDimensionWidth(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm" />
          </div>
          <div>
            <label className="block text-xs mb-1">Depth ({dimensionUnit})</label>
            <input type="number" min="0" step="0.01" value={dimensionDepth} onChange={(e) => setDimensionDepth(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm" />
          </div>
          <div>
            <label className="block text-xs mb-1">Weight ({measurementSystem === 'american' ? 'lb' : weightUnit})</label>
            <input type="number" min="0" step="0.01" value={weight} onChange={(e) => setWeight(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm" />
          </div>
          {measurementSystem === 'american' && (
            <div>
              <label className="block text-xs mb-1">Ounces</label>
              <input type="number" min="0" step="0.01" value={weightOunces} onChange={(e) => setWeightOunces(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm" />
            </div>
          )}
        </div>
        {(dimensionConversions.length > 0 || weightConversions.length > 0) && (
          <div className="rounded-lg border border-amber-450/20 bg-amber-450/5 px-3 py-2 text-xs text-gray-600 dark:text-warm-sand">
            <p className="font-medium text-gray-800 dark:text-warm-cream mb-1">Unit Conversion</p>
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              {[...dimensionConversions, ...weightConversions].map((conversion) => (
                <span key={conversion}>{conversion}</span>
              ))}
            </div>
          </div>
        )}
      </section>
    );
  };

  const renderStep = (step: SupplyFormStep = activeStep) => {
    switch (step) {
      case 'identity':
        return (
          <div className="space-y-6">
            <p className="text-sm text-gray-600 dark:text-warm-sand">
              What is this thing? Name it and classify it.
            </p>

            {/* Supply Name */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-900 dark:text-warm-cream mb-2">
                Product & Supply Name <span className="text-danger-400">*</span>
              </label>
              <input
                ref={nameInputRef}
                type="text"
                value={name}
                onFocus={() => setShowNameSuggestions(true)}
                onBlur={() => window.setTimeout(() => setShowNameSuggestions(false), 120)}
                onChange={(e) => handleNameChange(e.target.value)}
                placeholder="Start typing, e.g. Shirt, Dog Tag, Dragon, Axolotl..."
                className="w-full px-4 py-3 bg-white dark:bg-charcoal-800 border border-gray-300 dark:border-charcoal-600 rounded-xl text-gray-900 dark:text-warm-cream focus:outline-none focus:border-amber-450"
              />
              {showNameSuggestions && (
                <div className="absolute z-20 mt-2 w-full rounded-xl border border-gray-200 dark:border-charcoal-700 bg-white dark:bg-charcoal-850 shadow-xl overflow-hidden">
                  <div className="max-h-56 overflow-y-auto">
                    {filteredNameTemplates.map((template) => (
                      <button
                        key={template.name}
                        type="button"
                        onMouseDown={(event) => {
                          event.preventDefault();
                          applyNameTemplate(template);
                        }}
                        className="w-full px-4 py-3 text-left hover:bg-amber-450/10 transition-colors"
                      >
                        <span className="block text-sm font-medium text-gray-900 dark:text-warm-cream">{template.name}</span>
                        <span className="block text-xs text-gray-500 dark:text-warm-sand/70">
                          {SUPPLY_CLASSES.find((cls) => cls.value === template.supplyClass)?.label} / {CRAFT_TYPES_BY_CLASS[template.supplyClass].find((type) => type.value === template.supplyType)?.label}
                        </span>
                      </button>
                    ))}
                    {name.trim() && !selectedNameTemplate && (
                      <button
                        type="button"
                        onMouseDown={(event) => {
                          event.preventDefault();
                          addCustomSupplyName();
                        }}
                        className="w-full px-4 py-3 text-left border-t border-gray-200 dark:border-charcoal-700 hover:bg-blue-500/10 transition-colors"
                      >
                        <span className="block text-sm font-medium text-gray-900 dark:text-warm-cream">Add "{name.trim()}"</span>
                        <span className="block text-xs text-gray-500 dark:text-warm-sand/70">Then choose its Product & Supply Type and Supply Subtype.</span>
                      </button>
                    )}
                    {!filteredNameTemplates.length && !name.trim() && (
                      <p className="px-4 py-3 text-sm text-gray-500 dark:text-warm-sand">Start typing to search supply names.</p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {name.trim() ? (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Supply Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-900 dark:text-warm-cream mb-2">
                    Product & Supply Type
                  </label>
                  <select
                    id="supply-class-select"
                    value={supplyClass}
                    onChange={(e) => handleClassSelectChange(e.target.value)}
                    className="w-full px-4 py-3 bg-white dark:bg-charcoal-800 border border-gray-300 dark:border-charcoal-600 rounded-xl text-gray-900 dark:text-warm-cream focus:outline-none focus:border-amber-450"
                  >
                    <option value="">Select a type...</option>
                    {SUPPLY_CLASSES.map((cls) => (
                      <option key={cls.value} value={cls.value}>
                        {cls.emoji} {cls.label}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500 dark:text-warm-sand/60">
                    {SUPPLY_CLASSES.find((c) => c.value === supplyClass)?.description ?? 'Choose the broad category after naming the item.'}
                  </p>
                </div>

                {/* Supply Subtype */}
                {supplyClass && CRAFT_TYPES_BY_CLASS[supplyClass].length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-900 dark:text-warm-cream mb-2">
                      Supply Subtype
                    </label>
                    <select
                      value={supplyType}
                      onChange={(e) => handleSupplySubtypeChange(e.target.value as SupplyType)}
                      className="w-full px-4 py-3 bg-white dark:bg-charcoal-800 border border-gray-300 dark:border-charcoal-600 rounded-xl text-gray-900 dark:text-warm-cream focus:outline-none focus:border-amber-450"
                    >
                      <option value="">Select subtype...</option>
                      {CRAFT_TYPES_BY_CLASS[supplyClass].map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                )}
                {isApparelSupply && supplyType && (
                  <div>
                    <label className="block text-sm font-medium mb-2">Apparel Details</label>
                    <select value={apparelSize} onChange={(e) => setApparelSize(e.target.value)} className="w-full px-4 py-3 bg-white dark:bg-charcoal-800 border border-gray-300 dark:border-charcoal-600 rounded-xl text-gray-900 dark:text-warm-cream focus:outline-none focus:border-amber-450">
                      <option value="">Select size...</option>
                      {APPAREL_SIZES.map((size) => <option key={size} value={size}>{size}</option>)}
                    </select>
                    <p className="mt-1 text-xs text-gray-500 dark:text-warm-sand/60">
                      Only shown for Blank Apparel, like shirts and hoodies.
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="rounded-xl border border-dashed border-gray-300 dark:border-charcoal-700 bg-gray-50 dark:bg-charcoal-800/40 p-4 text-sm text-gray-600 dark:text-warm-sand">
                Product & Supply Type will appear after Product & Supply Name is entered.
              </div>
            )}

          </div>
        );

      case 'productionMethods':
        return (
          <div className="space-y-6">
            <section className="space-y-4">
              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-warm-cream">Production Method</h3>
                <p className="mt-1 text-sm text-gray-600 dark:text-warm-sand">How is this source used to make products?</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2">
                {relevantProductionMethods.map((method) => {
                  const selected = productionMethodIds.includes(method.id);
                  return (
                    <label
                      key={method.id}
                      className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer ${
                        selected ? 'border-amber-450 bg-amber-450/10' : 'border-gray-200 dark:border-charcoal-700'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={() => toggleId(productionMethodIds, method.id, setProductionMethodIds)}
                        className="rounded text-amber-450"
                      />
                      <span className="text-sm font-medium">{method.icon} {method.name}</span>
                    </label>
                  );
                })}
              </div>
              {relevantProductionMethods.length === 0 && (
                <p className="rounded-xl border border-amber-450/20 bg-amber-450/5 p-3 text-sm text-gray-600 dark:text-warm-sand">
                  No production methods match this source yet. Add or seed methods like 3D Printed, Laser Engraved, Laser Cut, Embroidered, DTF Printed, UV Printed, Vinyl Cut, CNC Machined, or Resin Printed.
                </p>
              )}
            </section>

            <section className="space-y-4 pt-4 border-t border-gray-200 dark:border-charcoal-700">
              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-warm-cream">Production System</h3>
                <p className="mt-1 text-sm text-gray-600 dark:text-warm-sand">Which system uses this source? Systems appear after selecting a method.</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2">
                {relevantMachines.map((machine) => {
                  const selected = compatibleMachineIds.includes(machine.id);
                  return (
                    <label key={machine.id} className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer ${selected ? 'border-amber-450 bg-amber-450/10' : 'border-gray-200 dark:border-charcoal-700'}`}>
                      <input type="checkbox" checked={selected} onChange={() => toggleId(compatibleMachineIds, machine.id, setCompatibleMachineIds)} className="rounded text-amber-450" />
                      <span className="text-sm font-medium">{machine.name}</span>
                    </label>
                  );
                })}
              </div>
              {relevantMachines.length === 0 && (
                <p className="rounded-xl border border-amber-450/20 bg-amber-450/5 p-3 text-sm text-gray-600 dark:text-warm-sand">
                  {productionMethodIds.length === 0
                    ? 'Select a production method first so the matching production systems can be shown.'
                    : 'No production systems are assigned to the selected method yet.'}
                </p>
              )}
            </section>
          </div>
        );

      case 'material':
        return (
          <div className="space-y-6">
            <div className="space-y-2">
              <p className="text-sm text-gray-600 dark:text-warm-sand">
                Describe the physical characteristics of the supply.
              </p>
              <p className="text-xs text-gray-500 dark:text-warm-sand/70">
                What is it made from? What colors does it contain? What does it look like? What are its dimensions? What physical characteristics matter for production?
              </p>
            </div>

            <section className="space-y-4">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-warm-cream">🧱 Material Information</h3>
              {showMaterialControls && (
                showTumblerBlankGuide ? (
                  <div className="space-y-3 rounded-xl border border-amber-450/20 bg-amber-450/5 p-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-sm font-medium mb-1">Product & Supply Subtype</label>
                        <select
                          value={tumblerProfileId}
                          onChange={(e) => applyTumblerProfile(e.target.value)}
                          className="w-full px-3 py-2 bg-white dark:bg-charcoal-800 border rounded-lg"
                        >
                          <option value="">Select subtype...</option>
                          {TUMBLER_BLANK_PROFILES.map((profile) => (
                            <option key={profile.id} value={profile.id}>{profile.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Physical Dimensions Preset</label>
                        <select
                          value={tumblerSizeLabel}
                          onChange={(e) => selectedTumblerProfile && applyTumblerProfile(selectedTumblerProfile.id, e.target.value)}
                          disabled={!selectedTumblerProfile}
                          className="w-full px-3 py-2 bg-white dark:bg-charcoal-800 border rounded-lg disabled:opacity-60"
                        >
                          <option value="">Select size...</option>
                          {selectedTumblerProfile?.sizes.map((size) => (
                            <option key={size.label} value={size.label}>{size.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 text-sm">
                      <div className="rounded-lg border border-gray-200 dark:border-charcoal-700 bg-white/70 dark:bg-charcoal-800/60 px-3 py-2">
                        <span className="block text-xs text-gray-500 dark:text-warm-sand/70">Material Type</span>
                        <span className="font-medium">{materialType || 'Prefilled after subtype selection'}</span>
                      </div>
                      <div className="rounded-lg border border-gray-200 dark:border-charcoal-700 bg-white/70 dark:bg-charcoal-800/60 px-3 py-2">
                        <span className="block text-xs text-gray-500 dark:text-warm-sand/70">Finish</span>
                        <span className="font-medium">{finish || 'Prefilled after subtype selection'}</span>
                      </div>
                      <div className="rounded-lg border border-gray-200 dark:border-charcoal-700 bg-white/70 dark:bg-charcoal-800/60 px-3 py-2">
                        <span className="block text-xs text-gray-500 dark:text-warm-sand/70">Coating & Surface</span>
                        <span className="font-medium">{surfaceTreatments.join(', ') || 'None selected'}</span>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-warm-sand/70">
                      Material and finish are prefilled here so mug/tumbler entries do not show the full material catalog.
                    </p>
                  </div>
                ) : (
                  <div className={`grid grid-cols-1 sm:grid-cols-2 ${showCoatingSurface && showFinishControl ? 'xl:grid-cols-3' : 'xl:grid-cols-2'} gap-4`}>
                    <div>
                      <label className="block text-sm font-medium mb-1">Material Type</label>
                      <select value={materialType} onChange={(e) => handleMaterialTypeChange(e.target.value)} className="w-full px-3 py-2 bg-white dark:bg-charcoal-800 border rounded-lg">
                        <option value="">Select material...</option>
                        {materialTypeOptions.map((m) => (
                          <option key={m} value={m}>{m}</option>
                        ))}
                      </select>
                    </div>
                    {showFinishControl && (
                      <div>
                        <label className="block text-sm font-medium mb-1">Finish</label>
                        <select value={finish} onChange={(e) => setFinish(e.target.value)} className="w-full px-3 py-2 bg-white dark:bg-charcoal-800 border rounded-lg">
                          <option value="">Select finish...</option>
                          {finishOptions.map((f) => <option key={f} value={f}>{f}</option>)}
                        </select>
                        {materialType && FINISH_OPTIONS_BY_MATERIAL[materialType] && (
                          <p className="mt-1 text-xs text-gray-500 dark:text-warm-sand/70">
                            Finish options are filtered for {materialType}.
                          </p>
                        )}
                      </div>
                      )}
                    {showCoatingSurface && (
                      <div>
                        <label className="block text-sm font-medium mb-1">Coating & Surface</label>
                        <select
                          value={surfaceTreatments[0] ?? ''}
                          onChange={(e) => setSurfaceTreatments(e.target.value ? [e.target.value] : [])}
                          className="w-full px-3 py-2 bg-white dark:bg-charcoal-800 border rounded-lg"
                        >
                          <option value="">Select coating or surface...</option>
                          {surfaceTreatmentOptions.map((treatment) => (
                            <option key={treatment} value={treatment}>{treatment}</option>
                          ))}
                        </select>
                        <p className="mt-1 text-xs text-gray-500 dark:text-warm-sand/70">
                          Options appear after Material Type is selected. Nothing is preselected.
                        </p>
                      </div>
                    )}
                  </div>
                )
              )}

              {renderPhysicalDimensionsSection()}

              <div>
                <div className="flex items-center justify-between gap-3 mb-2">
                  <label className="block text-sm font-medium">Colors</label>
                  <Button type="button" variant="secondary" onClick={() => setQuickAddType('color')} className="px-2 py-1 text-xs">
                    <Plus className="w-3 h-3 mr-1" /> Add Color
                  </Button>
                </div>
                {selectedColors.length > 0 && (
                  <div className="mb-3 rounded-xl border border-amber-450/20 bg-amber-450/5 p-3">
                    <p className="mb-2 text-xs font-medium text-gray-700 dark:text-warm-cream">Selected colors</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedColors.map((color) => {
                        const refColor = colors.find((item) => item.name === color);
                        return (
                          <span
                            key={color}
                            className="inline-flex items-center gap-1 rounded-full border border-amber-450 bg-amber-450/15 px-2 py-1 text-xs font-medium text-amber-700 dark:text-amber-300"
                          >
                            <button type="button" onClick={() => toggleColor(color)} className="inline-flex items-center gap-2">
                              <span
                                className="h-4 w-4 rounded-full border border-gray-300 dark:border-charcoal-600 shadow-inner"
                                style={swatchStyle(colorHexByName.get(color) ?? color)}
                                aria-hidden="true"
                              />
                              {color} ×
                            </button>
                            {refColor && (
                              <>
                                <button type="button" onClick={() => setReferenceEditTarget({ type: 'color', item: refColor })} className="rounded p-1 hover:bg-white/70 dark:hover:bg-charcoal-700" title={`Edit ${color}`}>
                                  <Edit2 className="h-3 w-3" />
                                </button>
                                <button type="button" onClick={() => void handleDeleteColor(color)} className="rounded p-1 hover:bg-danger-500/10 hover:text-danger-500" title={`Delete ${color}`}>
                                  <Trash2 className="h-3 w-3" />
                                </button>
                              </>
                            )}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_220px] gap-3">
                  <div>
                    <p className="mb-2 text-xs font-medium text-gray-500 dark:text-warm-sand/70">Standard colors</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                      {standardColorOptions.map(renderColorButton)}
                    </div>
                  </div>
                  <div>
                    <p className="mb-2 text-xs font-medium text-gray-500 dark:text-warm-sand/70">Metallics</p>
                    <div className="grid grid-cols-2 xl:grid-cols-1 gap-2">
                      {metallicColorOptions.map(renderColorButton)}
                    </div>
                  </div>
                </div>
                {colorSummary && (
                  <p className="mt-2 text-xs text-gray-500 dark:text-warm-sand/70">Result: {colorSummary}</p>
                )}
              </div>
            </section>

            {showPhysicalCharacteristics && (
            <section className="space-y-4 pt-4 border-t border-gray-200 dark:border-charcoal-700">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-warm-cream">🔷 Physical Characteristics</h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Shape</label>
                  <div className="flex gap-2">
                    <select value={blankShape} onChange={(e) => setBlankShape(e.target.value)} className="min-w-0 flex-1 px-3 py-2 border rounded-lg">
                      <option value="">Select shape...</option>
                      {shapeOptions.map((shape) => <option key={shape} value={shape}>{shape}</option>)}
                    </select>
                    <Button type="button" variant="secondary" onClick={handleAddShape} className="px-2" title="Add shape"><Plus className="w-4 h-4" /></Button>
                    <Button type="button" variant="secondary" onClick={handleEditShape} disabled={!blankShape} className="px-2" title="Edit shape"><Edit2 className="w-4 h-4" /></Button>
                    <Button type="button" variant="secondary" onClick={handleDeleteShape} disabled={!blankShape} className="px-2" title="Delete shape"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                  <p className="mt-1 text-xs text-gray-500 dark:text-warm-sand/70">
                    Shape defaults from Product & Supply Type when known.
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Surface Characteristics</label>
                  <div className="rounded-xl border border-gray-200 dark:border-charcoal-700 p-3">
                  <div className="flex flex-wrap gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={isDoubleSided} onChange={(e) => setIsDoubleSided(e.target.checked)} className="rounded text-amber-450" />
                      <span className="text-sm">Double-Sided</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={isCurvedSurface} onChange={(e) => setIsCurvedSurface(e.target.checked)} className="rounded text-amber-450" />
                      <span className="text-sm">Curved / Tapered Surface</span>
                    </label>
                  </div>
                  </div>
                </div>
              </div>
            </section>
            )}

            {supplyClass === 'CUSTOMIZABLE_BLANK' && (
              <section className="space-y-4 pt-4 border-t border-gray-200 dark:border-charcoal-700">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-warm-cream">🖨 Decoration Areas</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs mb-1">Printable Area (W × H)</label>
                    <div className="flex gap-2">
                      <input type="number" placeholder="W" value={printableAreaWidth} onChange={(e) => setPrintableAreaWidth(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm" />
                      <input type="number" placeholder="H" value={printableAreaHeight} onChange={(e) => setPrintableAreaHeight(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs mb-1">Engravable Area (W × H)</label>
                    <div className="flex gap-2">
                      <input type="number" placeholder="W" value={engravableAreaWidth} onChange={(e) => setEngravableAreaWidth(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm" />
                      <input type="number" placeholder="H" value={engravableAreaHeight} onChange={(e) => setEngravableAreaHeight(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm" />
                    </div>
                  </div>
                </div>
              </section>
            )}

            {supplyClass === 'CONSUMABLE' && supplyType === 'FILAMENT' && (
              <section className="space-y-4 pt-4 border-t border-gray-200 dark:border-charcoal-700">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-warm-cream">🖨 Filament Details</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs mb-1">Maker</label>
                    <select value={filamentMaker} onChange={(e) => setFilamentMaker(e.target.value)} className="w-full px-3 py-2 border rounded-lg">
                      <option value="">Select maker...</option>
                      {FILAMENT_MAKERS.map((maker) => <option key={maker} value={maker}>{maker}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs mb-1">Filament Type</label>
                    <select value={filamentType} onChange={(e) => setFilamentType(e.target.value)} className="w-full px-3 py-2 border rounded-lg">
                      <option value="">Select type...</option>
                      {FILAMENT_TYPES.map((type) => <option key={type} value={type}>{type}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs mb-1">Diameter</label>
                    <select value={filamentDiameter} onChange={(e) => setFilamentDiameter(e.target.value)} className="w-full px-3 py-2 border rounded-lg">
                      <option value="">Select diameter...</option>
                      {FILAMENT_DIAMETERS.map((diameter) => <option key={diameter} value={diameter}>{diameter}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs mb-1">Net Weight</label>
                    <select value={filamentNetWeight} onChange={(e) => setFilamentNetWeight(e.target.value)} className="w-full px-3 py-2 border rounded-lg">
                      <option value="">Select weight...</option>
                      {filamentNetWeightOptions.map((netWeight) => <option key={netWeight} value={netWeight}>{netWeight}</option>)}
                    </select>
                  </div>
                </div>
              </section>
            )}

            {supplyClass === 'HARDWARE_COMPONENT' && (
              <section className="space-y-4 pt-4 border-t border-gray-200 dark:border-charcoal-700">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-warm-cream">🔩 Hardware Details</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs mb-1">Hardware Type</label>
                    <select value={hardwareType} onChange={(e) => setHardwareType(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm">
                      <option value="">Select hardware type...</option>
                      {hardwareTypeOptions.map((type) => <option key={type} value={type}>{type}</option>)}
                    </select>
                  </div>
                  {hardwareDimensionFields.includes('outerDiameter') && (
                    <div>
                      <label className="block text-xs mb-1">{hardwareLookupMetadata.outerLabel || (hardwareType === 'Bail' ? 'Width' : hardwareType === 'Ball Chain' ? 'Bead Diameter' : 'Outer Diameter')}</label>
                      <input type="number" min="0" step="0.01" value={hardwareOuterDiameter} onChange={(e) => setHardwareOuterDiameter(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm" />
                    </div>
                  )}
                  {hardwareDimensionFields.includes('innerDiameter') && (
                    <div>
                      <label className="block text-xs mb-1">{hardwareLookupMetadata.innerLabel || (hardwareType === 'Bail' ? 'Height / Opening Size' : 'Inner Diameter')}</label>
                      <input type="number" min="0" step="0.01" value={hardwareInnerDiameter} onChange={(e) => setHardwareInnerDiameter(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm" />
                    </div>
                  )}
                  {hardwareDimensionFields.includes('wireThickness') && (
                    <div>
                      <label className="block text-xs mb-1">{hardwareLookupMetadata.wireLabel || (hardwareType === 'Magnet' ? 'Thickness' : 'Wire Thickness')}</label>
                      <input type="number" min="0" step="0.01" value={hardwareWireThickness} onChange={(e) => setHardwareWireThickness(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm" />
                    </div>
                  )}
                  {hardwareOpeningStyleOptions.length > 0 && (
                    <div>
                      <label className="block text-xs mb-1">Opening Style</label>
                      <select value={hardwareOpeningStyle} onChange={(e) => setHardwareOpeningStyle(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm">
                        <option value="">Select opening style...</option>
                        {hardwareOpeningStyleOptions.map((style) => <option key={style} value={style}>{style}</option>)}
                      </select>
                    </div>
                  )}
                  <div>
                    <label className="block text-xs mb-1">Finish / Plating Color</label>
                    <select value={colorSummary} onChange={(e) => setColorSummary(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm">
                      <option value="">Select finish color...</option>
                      {hardwareFinishColorOptions.map((color) => <option key={color} value={color}>{color}</option>)}
                    </select>
                  </div>
                </div>
                <p className="text-xs text-gray-500 dark:text-warm-sand/70">
                  Hardware Type controls which hardware measurements appear. Shapes and starter hardware types now come from saved lookup data.
                </p>
              </section>
            )}

          </div>
        );

      case 'productCompatibility':
        return (
          <div className="space-y-4">
            <p className="text-sm text-gray-600 dark:text-warm-sand">Which product types can use this supply?</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2">
              {itemTypes?.map((item) => {
                const selected = compatibleItemTypeIds.includes(item.id);
                return (
                  <label key={item.id} className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer ${selected ? 'border-amber-450 bg-amber-450/10' : 'border-gray-200 dark:border-charcoal-700'}`}>
                    <input type="checkbox" checked={selected} onChange={() => toggleId(compatibleItemTypeIds, item.id, setCompatibleItemTypeIds)} className="rounded text-amber-450" />
                    <span className="text-sm">{item.icon} {item.name}</span>
                  </label>
                );
              })}
            </div>
          </div>
        );

      case 'machineCompatibility':
        return (
          <div className="space-y-4">
            <p className="text-sm text-gray-600 dark:text-warm-sand">Which production system makes or processes this product/supply?</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2">
              {relevantMachines.map((machine) => {
                const selected = compatibleMachineIds.includes(machine.id);
                return (
                  <label key={machine.id} className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer ${selected ? 'border-amber-450 bg-amber-450/10' : 'border-gray-200 dark:border-charcoal-700'}`}>
                    <input type="checkbox" checked={selected} onChange={() => toggleId(compatibleMachineIds, machine.id, setCompatibleMachineIds)} className="rounded text-amber-450" />
                    <span className="text-sm font-medium">{machine.name}</span>
                  </label>
                );
              })}
            </div>
            {relevantMachines.length === 0 && (
              <p className="rounded-xl border border-amber-450/20 bg-amber-450/5 p-3 text-sm text-gray-600 dark:text-warm-sand">
                {productionMethodIds.length === 0
                  ? 'Select a production method first so the matching production systems can be shown.'
                  : 'No production systems are assigned to the selected method yet.'}
              </p>
            )}
          </div>
        );

      case 'tracking':
        return (
          <div className="space-y-4">
            <p className="text-sm text-gray-600 dark:text-warm-sand">Purchasing details for things you buy: SKUs, supplier, cost, stock, and storage.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
              <div>
                <label className="block text-sm font-medium mb-1"><Factory className="w-3 h-3 inline" /> Supplier SKU 1</label>
                <input value={manufacturerSku} onChange={(e) => setManufacturerSku(e.target.value)} className="w-full px-3 py-2 border rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Supplier SKU 2</label>
                <input value={manufacturerSku2} onChange={(e) => setManufacturerSku2(e.target.value)} className="w-full px-3 py-2 border rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Supplier SKU 3</label>
                <input value={manufacturerSku3} onChange={(e) => setManufacturerSku3(e.target.value)} className="w-full px-3 py-2 border rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Auto Generated SKU</label>
                <input value={supplierSku || 'Generated after save'} readOnly className="w-full px-3 py-2 border rounded-lg bg-gray-50 text-gray-500 dark:bg-charcoal-800/60 dark:text-warm-sand/70" />
                <p className="mt-1 text-xs text-gray-500 dark:text-warm-sand/70">System-controlled; this cannot be added manually.</p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1"><Link2 className="w-3 h-3 inline" /> Purchase link</label>
                <input type="url" value={purchaseUrl} onChange={(e) => setPurchaseUrl(e.target.value)} className="w-full px-3 py-2 border rounded-lg" />
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-3">
              <div>
                <label className="block text-xs mb-1">Unit type</label>
                <select value={unitOfMeasure} onChange={(e) => setUnitOfMeasure(e.target.value)} className="w-full px-3 py-2 border rounded-lg">
                  {unitOptions.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs mb-1">Qty in stock</label>
                <input type="number" min="0" step="0.01" value={quantityInStock} onChange={(e) => setQuantityInStock(e.target.value)} className="w-full px-3 py-2 border rounded-lg" />
              </div>
              <div>
                <label className="block text-xs mb-1">Cost / unit</label>
                <input type="number" min="0" step="0.0001" value={costPerUnit} onChange={(e) => setCostPerUnit(e.target.value)} className="w-full px-3 py-2 border rounded-lg" />
              </div>
              <div>
                <label className="block text-xs mb-1">Reorder at</label>
                <input type="number" min="0" step="0.01" value={reorderLevel} onChange={(e) => setReorderLevel(e.target.value)} className="w-full px-3 py-2 border rounded-lg" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 pt-2 border-t border-gray-200 dark:border-charcoal-700">
              <div>
                <label className="block text-sm font-medium mb-1"><ShoppingCart className="w-3 h-3 inline" /> Supplier</label>
                <div className="flex gap-2">
                  <select value={supplierId} onChange={(e) => { setSupplierId(e.target.value); setSupplier(suppliers.find((s) => s.id === e.target.value)?.name || ''); }} className="flex-1 px-3 py-2 border rounded-lg">
                    <option value="">Select supplier...</option>
                    {supplierOptions.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                  <Button type="button" variant="secondary" onClick={() => setQuickAddType('supplier')} className="px-2"><Plus className="w-4 h-4" /></Button>
                  <Button type="button" variant="secondary" onClick={() => {
                    const selected = suppliers.find((item) => item.id === supplierId);
                    if (selected) setReferenceEditTarget({ type: 'supplier', item: selected });
                  }} disabled={!supplierId} className="px-2"><Edit2 className="w-4 h-4" /></Button>
                  <Button type="button" variant="secondary" onClick={() => void handleDeleteSupplier()} disabled={!supplierId} className="px-2"><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 pt-2 border-t border-gray-200 dark:border-charcoal-700">
              <div>
                <label className="block text-sm font-medium mb-1">Inventory Location</label>
                <div className="flex gap-2">
                  <select
                    value={storageLocationId}
                    onChange={(e) => setStorageLocationId(e.target.value)}
                    className="min-w-0 flex-1 px-3 py-2 border rounded-lg"
                  >
                    <option value="">Select location...</option>
                    {storageLocationOptions.map((location) => (
                      <option key={location.id} value={location.id}>
                        {location.label}
                      </option>
                    ))}
                  </select>
                  <Button type="button" variant="secondary" onClick={() => setQuickAddType('location')} className="px-2" title="Add location or bin"><Plus className="w-4 h-4" /></Button>
                  <Button type="button" variant="secondary" onClick={() => {
                    const selected = storageLocationOptions.find((item) => item.id === storageLocationId);
                    if (selected) setReferenceEditTarget({ type: 'location', item: selected });
                  }} disabled={!storageLocationId} className="px-2" title="Edit location"><Edit2 className="w-4 h-4" /></Button>
                  <Button type="button" variant="secondary" onClick={() => void handleDeleteLocation()} disabled={!storageLocationId} className="px-2" title="Delete location"><Trash2 className="w-4 h-4" /></Button>
                </div>
                <p className="mt-1 text-xs text-gray-500 dark:text-warm-sand/70">
                  Sorted automatically. Select a parent location first, then Add to create a bin under it.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Storage Notes</label>
                <textarea
                  value={storageNotes}
                  onChange={(e) => setStorageNotes(e.target.value)}
                  rows={3}
                  placeholder="Bin notes, handling reminders, overflow details..."
                  className="w-full px-3 py-2 border rounded-lg resize-none"
                />
              </div>
            </div>
          </div>
        );

      case 'photo':
        return (
          <PhotoStepBoundary resetKey={`${isOpen}:${activeStep}:${imageUrl ?? 'no-photo'}`}>
            <div className="space-y-4">
              {renderSupplyPhotoUpload()}
              <div>
                <label className="block text-sm font-medium text-gray-900 dark:text-warm-cream mb-2">
                  Description
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  placeholder="Catalog-style description of the item..."
                  className="w-full px-4 py-3 bg-white dark:bg-charcoal-800 border border-gray-300 dark:border-charcoal-600 rounded-xl text-gray-900 dark:text-warm-cream focus:outline-none focus:border-amber-450 resize-none"
                />
              </div>
              <div className="pt-4 border-t border-gray-200 dark:border-charcoal-700">
                <label className="block text-sm font-medium text-gray-900 dark:text-warm-cream mb-2">
                  Notes
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  placeholder="Internal notes, observations, reminders, vendor comments, quirks..."
                  className="w-full px-4 py-3 bg-white dark:bg-charcoal-800 border border-gray-300 dark:border-charcoal-600 rounded-xl text-gray-900 dark:text-warm-cream focus:outline-none focus:border-amber-450 resize-none"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-warm-sand/70">
                  Saved for later reference; this field is intentionally kept at the end for now.
                </p>
              </div>
            </div>
          </PhotoStepBoundary>
        );

      default:
        return null;
    }
  };

  if (!isOpen) return null;

  const isLastStep = stepIndex === steps.length - 1;

  return (
    <>
      <QuickAddModal
        isOpen={quickAddType === 'supplier'}
        onClose={() => setQuickAddType(null)}
        onSave={handleQuickAddSupplier}
        title="Add Supplier"
        placeholder="Amazon"
        extraLabel="Website"
        extraPlaceholder="https://..."
        secondExtraLabel="Sort order (optional; lower appears first)"
        secondExtraPlaceholder="Leave blank for default"
      />
      <QuickAddModal
        isOpen={quickAddType === 'color'}
        onClose={() => setQuickAddType(null)}
        onSave={handleQuickAddColor}
        title="Add Color"
        placeholder="Neon Green"
        extraLabel="Hex"
        extraPlaceholder="#39FF14"
        secondExtraLabel="Sort order"
        secondExtraPlaceholder="500"
      />
      <QuickAddModal isOpen={quickAddType === 'location'} onClose={() => setQuickAddType(null)} onSave={handleQuickAddLocation} title="Add Inventory Location" placeholder={storageLocationId ? 'Bin 1' : 'Studio A'} extraLabel="Sort order" extraPlaceholder="1" />
      <QuickAddModal
        isOpen={!!referenceEditTarget}
        onClose={() => setReferenceEditTarget(null)}
        onSave={handleSaveReferenceEdit}
        title={referenceEditTarget?.type === 'supplier' ? 'Edit Supplier' : referenceEditTarget?.type === 'location' ? 'Edit Inventory Location' : 'Edit Color'}
        placeholder={referenceEditTarget?.type === 'supplier' ? 'Supplier name' : referenceEditTarget?.type === 'location' ? 'Location name' : 'Color name'}
        initialName={referenceEditTarget?.item?.name ?? ''}
        initialExtra={
          referenceEditTarget?.type === 'supplier'
            ? referenceEditTarget.item?.websiteUrl ?? ''
            : referenceEditTarget?.type === 'location'
              ? String(referenceEditTarget.item?.sortOrder ?? 0)
            : referenceEditTarget?.item && 'hexCode' in referenceEditTarget.item
              ? referenceEditTarget.item.hexCode
              : ''
        }
        initialSecondExtra={
          referenceEditTarget?.type === 'supplier'
            ? String(referenceEditTarget.item?.sortOrder ?? 100)
            : referenceEditTarget?.type === 'color'
              ? String(referenceEditTarget.item?.sortOrder ?? colorRank(referenceEditTarget.item?.name ?? ''))
              : ''
        }
        submitLabel="Save"
        extraLabel={referenceEditTarget?.type === 'supplier' ? 'Website' : referenceEditTarget?.type === 'location' ? 'Sort order' : 'Hex'}
        extraPlaceholder={referenceEditTarget?.type === 'supplier' ? 'https://...' : referenceEditTarget?.type === 'location' ? '1' : '#39FF14'}
        secondExtraLabel={referenceEditTarget?.type === 'supplier' || referenceEditTarget?.type === 'color' ? 'Sort order (optional; lower appears first)' : undefined}
        secondExtraPlaceholder={referenceEditTarget?.type === 'supplier' ? 'Leave blank for default' : referenceEditTarget?.type === 'color' ? '500' : undefined}
      />

      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm" onClick={!loading ? onClose : undefined} />
        <div className="relative w-full max-w-7xl max-h-[94vh] overflow-hidden rounded-2xl bg-white dark:bg-charcoal-900 border border-gray-200 dark:border-charcoal-700 shadow-2xl flex flex-col">
          <div className="flex flex-col gap-4 px-6 py-4 border-b border-gray-200 dark:border-charcoal-700 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center">
                <Package className="w-5 h-5 text-charcoal-900" />
              </div>
              <div>
                <h2 className="font-display text-lg font-bold">{isEditMode ? 'Edit Supply' : 'Add Supply'}</h2>
              </div>
            </div>
            <div className="flex items-center justify-between gap-3 lg:justify-end">
              {formMode === 'wizard' && (
                <div className="hidden items-center gap-2 text-xs text-gray-500 dark:text-warm-sand/70 sm:flex">
                  <span>{stepIndex + 1} of {steps.length}</span>
                  <div className="h-2 w-28 overflow-hidden rounded-full bg-gray-100 dark:bg-charcoal-700">
                    <div className="h-full bg-amber-450 transition-all" style={{ width: `${((stepIndex + 1) / steps.length) * 100}%` }} />
                  </div>
                </div>
              )}
              <div className="inline-flex rounded-lg border border-gray-200 dark:border-charcoal-600 p-0.5">
                <button
                  type="button"
                  onClick={() => handleModeChange('wizard')}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    formMode === 'wizard'
                      ? 'bg-amber-450/15 text-amber-600 dark:text-amber-400'
                      : 'text-gray-600 dark:text-warm-sand'
                  }`}
                >
                  <LayoutList className="w-4 h-4" />
                  Wizard
                </button>
                <button
                  type="button"
                  onClick={() => handleModeChange('single')}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    formMode === 'single'
                      ? 'bg-amber-450/15 text-amber-600 dark:text-amber-400'
                      : 'text-gray-600 dark:text-warm-sand'
                  }`}
                >
                  <ScrollText className="w-4 h-4" />
                  Single Page
                </button>
              </div>
              <button type="button" onClick={onClose} disabled={loading} className="p-2 hover:bg-gray-100 dark:hover:bg-charcoal-700 rounded-xl"><X className="w-5 h-5" /></button>
            </div>
          </div>

          {formMode === 'wizard' && (
            <div className="px-6 py-2 border-b border-gray-200 dark:border-charcoal-700 flex-shrink-0">
              <div className="flex gap-2 overflow-x-auto pb-1">
                {steps.map((step, index) => (
                  <button
                    key={step}
                    type="button"
                    onClick={() => {
                      if (index <= stepIndex || validateStep(activeStep)) {
                        setActiveStep(step);
                      }
                    }}
                    className={`min-w-fit rounded-xl border px-3 py-2 text-left text-xs transition-colors ${
                      step === activeStep
                        ? 'border-amber-450 bg-amber-450/10 text-amber-700 dark:text-amber-300'
                        : index < stepIndex
                          ? 'border-green-500/30 bg-green-500/5 text-green-700 dark:text-green-300'
                          : 'border-gray-200 dark:border-charcoal-700 text-gray-600 dark:text-warm-sand'
                    }`}
                  >
                    <span className="block font-semibold">{STEP_LABELS[step]}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {error && (
            <div className="mx-6 mt-4 p-3 bg-danger-500/10 border border-danger-500/30 rounded-lg text-sm text-danger-400">{error}</div>
          )}

          <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
            <div className="flex-1 overflow-y-auto p-6">
              {formMode === 'single' ? (
                <SupplySinglePageLayout steps={steps} renderStep={renderStep} />
              ) : (
                renderStep()
              )}
            </div>
            <div className="flex justify-between gap-3 px-6 py-4 border-t border-gray-200 dark:border-charcoal-700">
              <Button type="button" variant="secondary" onClick={onClose} disabled={loading}>Cancel</Button>
              <div className="flex gap-2">
                {formMode === 'wizard' && stepIndex > 0 && (
                  <Button type="button" variant="secondary" onClick={() => setActiveStep(steps[stepIndex - 1])} disabled={loading}>Back</Button>
                )}
                {formMode === 'wizard' && !isLastStep ? (
                  <Button
                    type="button"
                    variant="primary"
                    onClick={() => {
                      if (!validateStep(activeStep)) return;
                      setActiveStep(steps[stepIndex + 1]);
                    }}
                  >
                    Continue
                  </Button>
                ) : (
                  <Button type="submit" variant="primary" disabled={loading || !name.trim()}>
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4 mr-1 inline" />{isEditMode ? 'Save' : 'Completed'}</>}
                  </Button>
                )}
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
